import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import { FloorPlanCanvas } from './FloorPlanCanvas';
import { FixtureLayer } from './FixtureLayer';
import { StripLayer } from './StripLayer';
import { ControlLayer } from './ControlLayer';
import { RoomLayer } from './RoomLayer';
import { WallLayer } from './WallLayer';
import { LVProductLayer } from './LVProductLayer';
import { RoomCreationOverlay } from './RoomCreationOverlay';
import { SelectionBox, SelectionBoxRef } from './SelectionBox';
import { ToolStatusIndicator } from './ToolStatusIndicator';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useZoomGestures } from '@/components/floor-plans/hooks/useZoomGestures';
import { useDragHandler } from '../hooks/useDragHandler';
import { useLightingEffects } from '@/contexts/LightingEffectsContext';
import { toast } from 'sonner';
import { getRoomIdForPoint } from '@/utils/roomDetection';
import { useFloorPlanMutations } from '../hooks/useFloorPlanMutations';
import { reassignElementsBasedOnConfidence } from '@/services/roomAssignmentService';
import { logger } from '@/lib/logger';
import { createEmptyActions } from '@/types/lighting';
import { MeasurementOverlay } from '@/components/floor-plans/MeasurementOverlay';
import { GroupNameDialog } from './GroupNameDialog';
import { AnnotationLayer } from './AnnotationLayer';
import { AnnotationDrawingOverlay } from './AnnotationDrawingOverlay';
import { StripDrawingOverlay } from './StripDrawingOverlay';
import { ImageAnnotationUploader } from './ImageAnnotationUploader';
import { useAnnotationUndoRedo } from '@/hooks/useAnnotationUndoRedo';
import { annotationQueryKey, markAnnotationAsMutated } from '@/hooks/useAnnotations';
import type { Annotation } from '@/services/annotationService';
import { useUndoRedoStore } from '@/stores/undoRedoStore';
import { SafariImageFix } from './SafariImageFix';
import { FloorPlanEditWrapper, FloorPlanEditWrapperRef } from './FloorPlanEditWrapper';
import { FloorPlanEditControls } from './FloorPlanEditControls';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { WallSelectionMode } from '@/components/floor-plans/elevations/WallSelectionMode';
import { ElevationCreationModal } from '@/components/floor-plans/elevations/ElevationCreationModal';
import { Wall } from '@/utils/wallDetection';
import { useNavigate } from 'react-router-dom';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { collectElementPositions, ViewportBounds } from '@/utils/elementPositionCollector';
import { AlignmentGuidesOverlay } from './AlignmentGuidesOverlay';
import { dispatchPlacementEvent } from '../hooks/useUnifiedPlacement';
import { FloorPlanLegend } from './FloorPlanLegend';
import { LoadConnectionLayer } from './LoadConnectionLayer';
import { CableLayer } from './cables/CableLayer';
import { CablePathDrawingOverlay } from './cables/CablePathDrawingOverlay';
import { CablePathEditOverlay } from './cables/CablePathEditOverlay';
import { CableBoxPlacementOverlay } from './cables/CableBoxPlacementOverlay';
import { useCableJunctionsStandardized } from '@/hooks/entities/useCableJunctionsStandardized';
import { useCablePathsStandardized } from '@/hooks/entities/useCablePathsStandardized';
import type { LegendConfig, MarkerSizesConfig } from '@/types/floorPlanViewer';
import { moveAnnotationDataToAnchor } from '@/utils/annotationPosition';
import { useMergedCategories } from '@/features/product-library/hooks/useMergedCategories';

interface FloorPlanViewportProps {
  floorPlan: any;
  onProcessComplete?: () => void;
  onToolSelect?: (
    tool: 'select' | 'pan' | 'create-fixture' | 'create-strip' | 'create-panel' | 'create-room',
  ) => void;
  selectedTool?:
    | 'select'
    | 'pan'
    | 'create-fixture'
    | 'create-strip'
    | 'create-panel'
    | 'create-room';
  selectedControlType?: 'keypad' | 'dimmer' | 'switch' | 'auxiliary' | 'centralized-keypad' | null;
  isControlPlacementMode?: boolean;
  selectedProductCategory?: string | null;
  isProductPlacementMode?: boolean;
  onProductCategorySelect?: (category: string | null, subcategory?: string | null) => void;
  selectedFixtureCategory?: string | null;
  selectedFixtureSubcategory?: string | null;
  isCircuitMode?: boolean;
  isPublicView?: boolean;
  isMeasuring?: boolean;
  onMeasurementComplete?: (pixelDistance: number) => void;
  setIsMeasuring?: (value: boolean) => void;
  isElevationMode?: boolean;
  // Unified placement palette item
  placementItem?: {
    type: 'fixture' | 'control' | 'product';
    category: string;
    subcategory?: string;
    productLibraryId?: string;
    displayName: string;
    iconSvg?: string;
    iconUrl?: string;
    iconColor?: string;
  } | null;
}

export function FloorPlanViewport({
  floorPlan,
  onProcessComplete,
  onToolSelect,
  selectedTool = 'select',
  selectedControlType = null,
  isControlPlacementMode = false,
  selectedProductCategory = null,
  isProductPlacementMode = false,
  onProductCategorySelect,
  selectedFixtureCategory = null,
  selectedFixtureSubcategory = null,
  isCircuitMode = false,
  isPublicView = false,
  isMeasuring = false,
  onMeasurementComplete,
  setIsMeasuring,
  isElevationMode = false,
  placementItem = null,
}: FloorPlanViewportProps) {
  // Debug props on mount and update

  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Get store values and actions
  const viewport = useAppStore(state => state.floorPlan.viewport);
  const updateViewport = useAppStore(state => state.updateViewport);
  const setImageSize = useAppStore(state => state.setImageSize);
  const imageSize = useAppStore(state => state.floorPlan.imageSize);
  const clearAllSelections = useAppStore(state => state.clearAllSelections);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const hoveredEntityId = useAppStore(state => state.ui.hoveredEntityId);
  const aiTools = useAppStore(state => state.aiTools);
  const selectedFixtures = useAppStore(state => state.selection.fixtures);
  const selectedControls = useAppStore(state => state.selection.controls);
  const activeTool = useAppStore(state => state.ui.toolbar.activeTool);
  const setSpacePressedStore = useAppStore(state => state.setSpacePressed);
  const isEditingFloorPlanImage = useAppStore(state => state.ui.isEditingFloorPlanImage);
  const setEditingFloorPlanImage = useAppStore(state => state.setEditingFloorPlanImage);
  const activeAnnotationType = useAppStore(state => state.annotations.activeAnnotationType);
  const isAnnotating = useAppStore(state => state.annotations.isAnnotating);
  const selectedRooms = useAppStore(state => state.selection.rooms);

  // Visibility state for filtering snappable elements
  const showOnlyUnassigned = useAppStore(state => state.ui.toolbar.showOnlyUnassigned);
  const showOnlyUnassignedControls = useAppStore(
    state => state.ui.toolbar.showOnlyUnassignedControls,
  );
  const roomsVisible = useAppStore(state => state.rooms.roomsVisible);
  const showAnnotations = useAppStore(state => state.annotations.showAnnotations);
  const setRoomVisibility = useAppStore(state => state.setRoomVisibility);

  // Get scale data to force remount of MeasurementOverlay when scale changes
  const { scaleData, hasScale, formatMeasurement } = useFloorPlanScale(floorPlan.id);

  // ✅ Lighting effects
  const { sim, stopSim } = useLightingEffects();

  // Navigation for elevation view
  const navigate = useNavigate();

  // Elevation state
  const [showElevationModal, setShowElevationModal] = useState(false);
  const [selectedWallData, setSelectedWallData] = useState<{
    roomId: string;
    roomName: string;
    wall: Wall;
    wallLength: number;
  } | null>(null);

  // Get shared floor plan metadata from context (for real-time updates from settings drawer)
  const { data, getDefaultScale, temporaryImageOpacity, elementSizeDefaultsLoading } =
    useFloorPlanContext();

  // Extract metadata-related values from shared context
  const {
    floorPlanMetadata,
    isMetadataLoading,
    handleImageLoad: handleMetadataImageLoad,
    saveFloorPlanMetadata,
  } = data;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [initialViewportSettled, setInitialViewportSettled] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [spacePressed, setSpacePressed] = useState(false);
  const selectionBoxRef = useRef<SelectionBoxRef>(null);
  const touchStartCount = useRef(0);
  const placementDelayTimer = useRef<NodeJS.Timeout | null>(null);
  const initialFitTimerRef = useRef<number | null>(null);
  const initialFitFrameRef = useRef<number | null>(null);
  const initialFitSecondFrameRef = useRef<number | null>(null);
  const isSelectionInProgress = useRef(false);
  const editWrapperRef = useRef<FloorPlanEditWrapperRef>(null);
  const roomsVisibleBeforePlacement = useRef<boolean | null>(null);

  // Group placement state
  const [showGroupNameDialog, setShowGroupNameDialog] = useState(false);
  const [pendingGroupPosition, setPendingGroupPosition] = useState<{
    x: number;
    y: number;
    roomId: string | null;
  } | null>(null);

  // Get mutations
  const {
    updateFixtureMutation,
    updateControlMutation,
    updateRoomMutation,
    updateProductMutation,
  } = useFloorPlanMutations(floorPlan.id);

  // Get cable junction operations for drag handling and deletion
  const { junctions, junctionCoordMap, moveJunction, deleteJunction } =
    useCableJunctionsStandardized(floorPlan.id);

  // Get cable path operations for deletion
  const { deletePath } = useCablePathsStandardized(floorPlan.id, junctionCoordMap, scaleData);

  // Get undo/redo functionality
  const { performUndo, performRedo, canUndo, canRedo } = useAnnotationUndoRedo(floorPlan.id);
  const addAction = useUndoRedoStore(state => state.addAction);

  const { isLoading: fixturesCategoriesLoading } = useMergedCategories('lighting_fixtures');
  const { isLoading: controlsCategoriesLoading } = useMergedCategories('lighting_controls');
  const { isLoading: lvCategoriesLoading } = useMergedCategories('lv_products');
  const areCategoryIconsReady = !(
    fixturesCategoriesLoading ||
    controlsCategoriesLoading ||
    lvCategoriesLoading
  );

  // Combined loading state - wait for image, metadata, AND all entity data to load
  // This ensures all icons appear together instead of "popping up" one-by-one
  const isFullyLoaded =
    imageLoaded &&
    initialViewportSettled &&
    !isMetadataLoading &&
    !data.isLoading &&
    !elementSizeDefaultsLoading &&
    areCategoryIconsReady;

  // Use store values directly instead of local state
  const position = { x: viewport.positionX, y: viewport.positionY };
  const zoom = viewport.scale;

  // Create setters that update the store
  const setPosition = useCallback(
    (newPosition: { x: number; y: number }) => {
      updateViewport({ positionX: newPosition.x, positionY: newPosition.y });
    },
    [updateViewport],
  );

  const setZoom = useCallback(
    (newZoom: number) => {
      updateViewport({ scale: newZoom });
    },
    [updateViewport],
  );

  const clearInitialFitSchedule = useCallback(() => {
    if (initialFitTimerRef.current !== null) {
      window.clearTimeout(initialFitTimerRef.current);
      initialFitTimerRef.current = null;
    }
    if (initialFitFrameRef.current !== null) {
      window.cancelAnimationFrame(initialFitFrameRef.current);
      initialFitFrameRef.current = null;
    }
    if (initialFitSecondFrameRef.current !== null) {
      window.cancelAnimationFrame(initialFitSecondFrameRef.current);
      initialFitSecondFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    setImageLoaded(false);
    setInitialViewportSettled(false);
    setImageError(null);
    clearInitialFitSchedule();

    return clearInitialFitSchedule;
  }, [floorPlan.id, data.imageUrl, data.svgUrl, clearInitialFitSchedule]);

  // Handle wall selection for elevation creation or navigation
  const handleWallSelect = useCallback(
    async (roomId: string, roomName: string, wall: Wall) => {
      try {
        // Check if an elevation already exists for this wall
        const supabase = await getAuthenticatedSupabaseClient();
        const { data: existingElevation, error } = await supabase
          .from('elevations')
          .select('id')
          .eq('floor_plan_id', floorPlan.id)
          .eq('room_id', roomId)
          .eq('wall_index', wall.index)
          .maybeSingle();

        if (error) {
          logger.error('Error checking for existing elevation:', error);
          toast.error('Failed to check for existing elevation');
          return;
        }

        if (existingElevation) {
          // Navigate directly to the existing elevation
          navigate(`/elevation/${existingElevation.id}`);
        } else {
          // No existing elevation, show creation modal
          const wallLength = scaleData
            ? wall.length / (scaleData.pixelsPerUnit || 100)
            : wall.length;

          setSelectedWallData({
            roomId,
            roomName,
            wall,
            wallLength,
          });
          setShowElevationModal(true);
        }
      } catch (err) {
        logger.error('Error in handleWallSelect:', err);
        toast.error('An error occurred');
      }
    },
    [scaleData, floorPlan.id, navigate],
  );

  // Handle elevation creation
  const handleElevationCreated = useCallback(
    (elevationId: string) => {
      // Navigate to the elevation editor
      const projectId = floorPlan.project_id;
      navigate(`/projects/${projectId}/plans/${floorPlan.id}/elevations/${elevationId}`);
    },
    [floorPlan.id, floorPlan.project_id, navigate],
  );

  // Handle legend config changes (position, visibility)
  const handleLegendConfigChange = useCallback(
    (updates: Partial<LegendConfig>) => {
      const currentMarkerSizes = (floorPlanMetadata?.marker_sizes as MarkerSizesConfig) || {};
      const currentLegendConfig = currentMarkerSizes.legend_config || {
        visible: true,
        x: 50,
        y: 50,
      };

      const newLegendConfig: LegendConfig = {
        ...currentLegendConfig,
        ...updates,
      };

      saveFloorPlanMetadata({
        marker_sizes: {
          ...currentMarkerSizes,
          legend_config: newLegendConfig,
        },
      });
    },
    [floorPlanMetadata, saveFloorPlanMetadata],
  );

  // Handle group creation with name
  const handleGroupCreate = useCallback(
    async (name: string) => {
      if (!pendingGroupPosition) return;

      try {
        const { x, y, roomId } = pendingGroupPosition;

        // Get default scale from cache (instant, no async)
        const defaultScale = getDefaultScale('product', 'group', undefined);

        const productData = {
          floor_plan_id: floorPlan.id,
          category: 'group' as const,
          name: name,
          x_position: x,
          y_position: y,
          rotation: 0,
          scale: defaultScale || 1, // Apply cached default scale immediately
          room_id: roomId,
        };

        logger.log('Creating LV group with data:', productData);
        // Use optimistic mutation from context (has instant UI updates)
        const result = await data.createProduct(productData, { showSuccessToast: false });

        if (!result) {
          throw new Error('Failed to create group');
        }

        // Dispatch placement event for counting
        dispatchPlacementEvent('product', 'group');

        // No need to refetch - optimistic updates handle this automatically

        // Auto-toggle off group placement mode after successful creation
        if (onProductCategorySelect) {
          onProductCategorySelect(null);
        }
      } catch (error) {
        logger.error('Failed to create group:', error);
        toast.error('Failed to create group');
      } finally {
        setPendingGroupPosition(null);
      }
    },
    [pendingGroupPosition, floorPlan.id, data, onProductCategorySelect, getDefaultScale],
  );

  // Filter elements by visibility for snapping calculations
  // Only visible elements should participate in snapping

  // Filter fixtures by confidence threshold and unassigned filter
  const visibleFixtures = useMemo(() => {
    if (!data.fixtures) return [];

    return data.fixtures.filter(fixture => {
      // Apply confidence filtering for AI-detected fixtures
      const isManuallyPlaced = fixture.confidence_score === 1 || fixture.user_verified === true;
      const isAIDetected =
        !isManuallyPlaced && fixture.confidence_score != null && fixture.confidence_score < 1;

      if (isAIDetected) {
        const confidence = fixture.confidence_score || 0;
        if (confidence < aiTools.confidenceThreshold) {
          return false; // Skip invisible AI fixtures
        }
      }

      // Apply unassigned filter
      if (showOnlyUnassigned && fixture.circuit_id) {
        return false; // Skip assigned fixtures when filter is on
      }

      return true; // Fixture is visible
    });
  }, [data.fixtures, aiTools.confidenceThreshold, showOnlyUnassigned]);

  // Filter controls by unassigned filter
  const visibleControls = useMemo(() => {
    if (!data.controls) return [];

    return data.controls.filter(control => {
      // Apply unassigned filter
      if (showOnlyUnassignedControls && control.circuit_id) {
        return false;
      }
      return true;
    });
  }, [data.controls, showOnlyUnassignedControls]);

  // Filter rooms by visibility toggle and confidence threshold
  const visibleRooms = useMemo(() => {
    if (!data.rooms) return [];
    if (!roomsVisible) return []; // Global toggle off

    return data.rooms.filter(room => {
      // Apply confidence filtering
      const confidence = room.confidence ?? 1;
      if (confidence < aiTools.roomConfidenceThreshold) {
        return false;
      }
      return true;
    });
  }, [data.rooms, roomsVisible, aiTools.roomConfidenceThreshold]);

  // Filter rooms by confidence only (for elevation tool - ignores visibility toggle)
  // This is separate from visibleRooms because elevation mode sets roomsVisible to false
  const confidenceFilteredRooms = useMemo(() => {
    if (!data.rooms) return [];
    return data.rooms.filter(room => {
      const confidence = room.confidence ?? 1;
      return confidence >= aiTools.roomConfidenceThreshold;
    });
  }, [data.rooms, aiTools.roomConfidenceThreshold]);

  // Filter annotations by visibility toggle
  const visibleAnnotations = useMemo(() => {
    if (!data.annotations) return [];
    if (!showAnnotations) return []; // Global toggle off
    return data.annotations;
  }, [data.annotations, showAnnotations]);

  // LV Products - no visibility filters currently
  const visibleProducts = data.lvProducts || [];

  // Collect all element positions for snap calculations
  // Only include elements within or near the visible viewport to improve snap performance
  // and prevent snapping to far off-screen elements
  const allElementPositions = useMemo(() => {
    // Calculate viewport bounds in image coordinates
    const container = containerRef.current;
    if (!container) {
      // No viewport filtering if container not available yet
      return collectElementPositions(
        visibleFixtures,
        visibleControls,
        visibleProducts,
        visibleAnnotations,
        visibleRooms,
      );
    }

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Convert screen viewport to image coordinates
    // Screen position (0, 0) in image coordinates:
    const minX = (0 - position.x) / zoom;
    const minY = (0 - position.y) / zoom;

    // Screen position (containerWidth, containerHeight) in image coordinates:
    const maxX = (containerWidth - position.x) / zoom;
    const maxY = (containerHeight - position.y) / zoom;

    const viewportBounds: ViewportBounds = {
      minX,
      maxX,
      minY,
      maxY,
    };

    return collectElementPositions(
      visibleFixtures,
      visibleControls,
      visibleProducts,
      visibleAnnotations,
      visibleRooms,
      [],
      viewportBounds,
    );
  }, [
    visibleFixtures,
    visibleControls,
    visibleProducts,
    visibleAnnotations,
    visibleRooms,
    zoom,
    position,
  ]);

  const cloneForHistory = useCallback(
    (value: any) => (value === undefined ? null : JSON.parse(JSON.stringify(value))),
    [],
  );

  const createHistoryData = useCallback(
    (source: any, updates: Record<string, any>) =>
      Object.keys(updates).reduce(
        (historyData, key) => ({
          ...historyData,
          [key]: cloneForHistory(source?.[key]),
        }),
        {},
      ),
    [cloneForHistory],
  );

  const applyFixtureOptimisticUpdate = useCallback(
    (id: string, updates: Record<string, any>) => {
      const queryKey = queryKeys.fixtures(floorPlan.id);
      const previousFixtures = queryClient.getQueryData<any[]>(queryKey);

      queryClient.setQueryData<any[]>(queryKey, (old = []) =>
        Array.isArray(old)
          ? old.map(item =>
              item.id === id
                ? {
                    ...item,
                    ...updates,
                    updated_at: new Date().toISOString(),
                  }
                : item,
            )
          : old,
      );

      return previousFixtures;
    },
    [floorPlan.id, queryClient],
  );

  const restoreFixtureOptimisticUpdate = useCallback(
    (previousFixtures: any[] | undefined) => {
      const queryKey = queryKeys.fixtures(floorPlan.id);
      if (previousFixtures) {
        queryClient.setQueryData(queryKey, previousFixtures);
      } else {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    [floorPlan.id, queryClient],
  );

  // Handle position updates for dragged entities
  const handleUpdatePosition = useCallback(
    async (
      id: string,
      x: number,
      y: number,
      type: 'fixture' | 'control' | 'panel' | 'room' | 'product' | 'annotation' | 'cableJunction',
    ) => {
      try {
        logger.log(`Updating ${type} ${id} position to:`, { x, y });

        switch (type) {
          case 'fixture': {
            // Check which room the fixture is now in (only visible rooms based on confidence)
            const roomId = getRoomIdForPoint(
              { x, y },
              data.rooms || [],
              aiTools.roomConfidenceThreshold,
            );
            const fixture = data.fixtures?.find(f => f.id === id);

            // Check if this is a strip (linear fixture) that needs delta-based translation
            const element = document.querySelector(`[data-fixture-id="${id}"]`) as HTMLElement;
            const isStrip =
              element?.dataset.isStrip === 'true' && fixture?.fixture_form_factor === 'linear';

            let updates: any;
            if (isStrip && element?.dataset.oldLeft && element?.dataset.oldTop) {
              // Strip: compute delta from old wrapper position to new position
              const oldLeft = parseFloat(element.dataset.oldLeft);
              const oldTop = parseFloat(element.dataset.oldTop);
              const deltaX = x - oldLeft;
              const deltaY = y - oldTop;

              updates = {
                x_position: fixture!.x_position + deltaX,
                y_position: fixture!.y_position + deltaY,
              };

              // Translate end position
              if (fixture!.end_x_position != null && fixture!.end_y_position != null) {
                updates.end_x_position = fixture!.end_x_position + deltaX;
                updates.end_y_position = fixture!.end_y_position + deltaY;
              }

              // Translate all polyline points
              if (fixture!.strip_points && fixture!.strip_points.length > 0) {
                updates.strip_points = fixture!.strip_points.map((p: { x: number; y: number }) => ({
                  x: p.x + deltaX,
                  y: p.y + deltaY,
                }));
              }

              // Clean up dataset flags
              delete element.dataset.isStrip;
              delete element.dataset.oldLeft;
              delete element.dataset.oldTop;
            } else {
              // Regular fixture: use absolute position
              updates = { x_position: x, y_position: y };
            }

            if (fixture && fixture.room_id !== roomId) {
              updates.room_id = roomId;
              logger.log(`Fixture moved to room: ${roomId}`);
            }

            if (fixture) {
              addAction({
                type: 'UPDATE_FIXTURE',
                timestamp: Date.now(),
                floorPlanId: floorPlan.id,
                data: {
                  id,
                  oldData: createHistoryData(fixture, updates),
                  newData: cloneForHistory(updates),
                },
              });
            }

            updateFixtureMutation.mutate({ id, updates, showSuccessToast: false });

            break;
          }
          case 'control': {
            // Check which room the control is now in (only visible rooms based on confidence)
            const controlRoomId = getRoomIdForPoint(
              { x, y },
              data.rooms || [],
              aiTools.roomConfidenceThreshold,
            );
            const control = data.controls?.find(c => c.id === id);

            // Update position and room assignment if changed
            const controlUpdates: any = { x, y };
            if (control && (control as any).room_id !== controlRoomId) {
              controlUpdates.room_id = controlRoomId;
              logger.log(`Control moved to room: ${controlRoomId}`);
            }

            if (control) {
              const controlHistorySource = {
                ...control,
                x: (control as any).x ?? (control as any).xPosition ?? (control as any).x_position,
                y: (control as any).y ?? (control as any).yPosition ?? (control as any).y_position,
              };
              addAction({
                type: 'UPDATE_CONTROL',
                timestamp: Date.now(),
                floorPlanId: floorPlan.id,
                data: {
                  id,
                  oldData: createHistoryData(controlHistorySource, controlUpdates),
                  newData: cloneForHistory(controlUpdates),
                },
              });
            }

            updateControlMutation.mutate({
              id,
              updates: controlUpdates,
              showSuccessToast: false,
            });

            break;
          }
          case 'panel':
            // await panelService.updatePosition(id, x, y);
            logger.log('Would update panel position via service');
            break;
          case 'room':
            // Rooms don't have a simple x,y position - they have polygon points
            // This should be handled differently, but for now we'll skip it
            logger.log('Room dragging is handled through polygon point updates');
            break;
          case 'product': {
            // Check which room the product is now in (only visible rooms based on confidence)
            const productRoomId = getRoomIdForPoint(
              { x, y },
              data.rooms || [],
              aiTools.roomConfidenceThreshold,
            );
            const product = data.lvProducts?.find(p => p.id === id);

            // Update position and room assignment
            const productUpdates: any = {
              x_position: x,
              y_position: y,
              room_id: productRoomId || null,
            };

            logger.log(`Updating product position:`, productUpdates);

            if (product) {
              addAction({
                type: 'UPDATE_PRODUCT',
                timestamp: Date.now(),
                floorPlanId: floorPlan.id,
                data: {
                  id,
                  oldData: createHistoryData(product, productUpdates),
                  newData: cloneForHistory(productUpdates),
                },
              });
            }

            // Use mutation instead of direct service call for real-time updates
            updateProductMutation.mutate({ id, updates: productUpdates });

            break;
          }
          case 'annotation': {
            // Get the annotation to update
            const annotation = data.annotations?.find(a => a.id === id);
            if (!annotation) {
              logger.error('Annotation not found:', id);
              return;
            }

            logger.log('Updating annotation position:', {
              id,
              type: annotation.type,
              oldData: annotation.data,
              newPosition: { x, y },
            });

            const updatedData = moveAnnotationDataToAnchor(annotation.type, annotation.data, {
              x,
              y,
            });

            // Update annotation in database with optimistic update
            const { updateAnnotation } = data;
            if (updateAnnotation) {
              // Apply the final position synchronously before the drag state is
              // cleared. React Query's mutation onMutate awaits cancelQueries,
              // which can leave a short frame where the UI falls back to the
              // old cached position after a fast drag release.
              markAnnotationAsMutated(id);
              queryClient.setQueryData(
                annotationQueryKey(floorPlan.id, 'floor_plan'),
                (oldAnnotations: Annotation[] | undefined) =>
                  Array.isArray(oldAnnotations)
                    ? oldAnnotations.map(item =>
                        item.id === id
                          ? {
                              ...item,
                              data: {
                                ...item.data,
                                ...updatedData,
                              },
                              updated_at: new Date().toISOString(),
                            }
                          : item,
                      )
                    : oldAnnotations,
              );

              logger.log('Calling updateAnnotation with:', {
                id,
                updatedData,
                originalData: annotation.data,
              });
              await updateAnnotation({ id, data: { data: updatedData } });
              logger.log('updateAnnotation completed');
            } else {
              logger.error('updateAnnotation function not available');
            }
            break;
          }
          case 'cableJunction': {
            // Move the cable junction to new position
            logger.log('Moving cable junction:', { id, x, y });
            const junction = junctions.find(item => item.id === id);
            if (junction) {
              addAction({
                type: 'UPDATE_CABLE_JUNCTION',
                timestamp: Date.now(),
                floorPlanId: floorPlan.id,
                data: {
                  id,
                  oldData: { x: junction.x, y: junction.y },
                  newData: { x, y },
                },
              });
            }
            await moveJunction(id, x, y);
            break;
          }
        }

        // Don't refetch immediately - let optimistic updates work
        // The data will refetch after staleTime expires
      } catch (error) {
        logger.error(`Failed to update ${type} position:`, error);
        if (type === 'annotation') {
          queryClient.invalidateQueries({
            queryKey: annotationQueryKey(floorPlan.id, 'floor_plan'),
          });
        }
        throw error;
      }
    },
    [
      addAction,
      cloneForHistory,
      createHistoryData,
      data,
      floorPlan.id,
      junctions,
      moveJunction,
      queryClient,
      updateControlMutation,
      updateFixtureMutation,
      updateProductMutation,
    ],
  );

  // Initialize drag handler
  const {
    isDragging,
    dragState,
    startDrag,
    endDrag,
    wasRecentlyDragged,
    screenToImageCoordinates,
  } = useDragHandler({
    containerRef,
    onUpdatePosition: handleUpdatePosition,
    allElements: allElementPositions,
  });

  // Handle strip endpoint drag for extending/shortening strips
  // Receives image coordinates (already converted by StripLayer)
  const handleStripEndpointDrag = useCallback(
    (stripId: string, endpoint: 'start' | 'end', imageX: number, imageY: number) => {
      // Find the strip
      const strip = data.fixtures?.find(f => f.id === stripId);
      if (!strip) return;

      // Build updates for the individual coordinate fields
      const updates: Record<string, any> = {};
      if (endpoint === 'start') {
        updates.x_position = imageX;
        updates.y_position = imageY;
      } else {
        updates.end_x_position = imageX;
        updates.end_y_position = imageY;
      }

      // Also update strip_points if they exist (they take precedence in rendering)
      if (strip.strip_points && strip.strip_points.length > 1) {
        const newPoints = [...strip.strip_points];
        if (endpoint === 'start') {
          newPoints[0] = { x: imageX, y: imageY };
        } else {
          newPoints[newPoints.length - 1] = { x: imageX, y: imageY };
        }
        updates.strip_points = newPoints;
      }

      addAction({
        type: 'UPDATE_FIXTURE',
        timestamp: Date.now(),
        floorPlanId: floorPlan.id,
        data: {
          id: stripId,
          oldData: createHistoryData(strip, updates),
          newData: cloneForHistory(updates),
        },
      });

      const previousFixtures = applyFixtureOptimisticUpdate(stripId, updates);

      // Update the fixture position
      void Promise.resolve(
        updateFixtureMutation.mutate({ id: stripId, updates, showSuccessToast: false }),
      ).catch(() => restoreFixtureOptimisticUpdate(previousFixtures));
    },
    [
      addAction,
      applyFixtureOptimisticUpdate,
      cloneForHistory,
      createHistoryData,
      data.fixtures,
      floorPlan.id,
      restoreFixtureOptimisticUpdate,
      updateFixtureMutation,
    ],
  );

  const handleStripVertexDrag = useCallback(
    (stripId: string, index: number, imageX: number, imageY: number) => {
      const strip = data.fixtures?.find(f => f.id === stripId);
      if (!strip?.strip_points || strip.strip_points.length <= index || index < 0) return;

      const newPoints = strip.strip_points.map((point, pointIndex) =>
        pointIndex === index ? { x: imageX, y: imageY } : point,
      );

      const updates: Record<string, any> = {
        strip_points: newPoints,
      };

      if (index === 0) {
        updates.x_position = imageX;
        updates.y_position = imageY;
      }

      if (index === newPoints.length - 1) {
        updates.end_x_position = imageX;
        updates.end_y_position = imageY;
      }

      addAction({
        type: 'UPDATE_FIXTURE',
        timestamp: Date.now(),
        floorPlanId: floorPlan.id,
        data: {
          id: stripId,
          oldData: createHistoryData(strip, updates),
          newData: cloneForHistory(updates),
        },
      });

      const previousFixtures = applyFixtureOptimisticUpdate(stripId, updates);

      void Promise.resolve(
        updateFixtureMutation.mutate({ id: stripId, updates, showSuccessToast: false }),
      ).catch(() => restoreFixtureOptimisticUpdate(previousFixtures));
    },
    [
      addAction,
      applyFixtureOptimisticUpdate,
      cloneForHistory,
      createHistoryData,
      data.fixtures,
      floorPlan.id,
      restoreFixtureOptimisticUpdate,
      updateFixtureMutation,
    ],
  );

  // State to track if we're currently panning
  const [isPanning, setIsPanning] = useState(false);

  // Determine cursor based on selected tool and control placement mode
  const getCursor = useCallback(() => {
    if (isPanning || (isDragging && (spacePressed || activeTool === 'pan'))) return 'grabbing';
    if (spacePressed || activeTool === 'pan') return 'grab';
    if (selectedTool === 'create-fixture') return 'crosshair';
    if (selectedTool === 'create-strip') return 'crosshair';
    if (isControlPlacementMode) return 'crosshair';
    if (activeTool === 'draw') return 'crosshair';
    if (activeTool === 'measure' || isMeasuring) return 'crosshair';
    if (hoveredEntityId) return 'pointer';
    return 'default';
  }, [
    isPanning,
    isDragging,
    spacePressed,
    activeTool,
    selectedTool,
    isControlPlacementMode,
    hoveredEntityId,
    isMeasuring,
  ]);

  // Sync local state with store (only after fully loaded to prevent initial 100% zoom)
  useEffect(() => {
    if (isFullyLoaded) {
      updateViewport({
        scale: zoom,
        positionX: position.x,
        positionY: position.y,
      });
    }
  }, [zoom, position, updateViewport, isFullyLoaded]);

  // Reset edit mode when floor plan changes
  useEffect(() => {
    // Always start with edit mode disabled when changing floor plans
    if (isEditingFloorPlanImage) {
      setEditingFloorPlanImage(false);
    }
  }, [floorPlan.id]); // Only depend on floorPlan.id, not setEditingFloorPlanImage to avoid infinite loop

  // Auto-hide rooms when any placement tool is active
  useEffect(() => {
    const isInPlacementMode =
      selectedTool === 'create-fixture' || // Fixture placement
      isControlPlacementMode || // Control placement
      selectedProductCategory !== null || // LV Product placement
      isAnnotating || // Annotation placement
      isCircuitMode; // Load/circuit creation

    if (isInPlacementMode) {
      // Entering placement mode: save current state and hide rooms
      if (roomsVisibleBeforePlacement.current === null) {
        roomsVisibleBeforePlacement.current = roomsVisible;
        setRoomVisibility(false);
      }
    } else {
      // Exiting placement mode: restore previous state
      if (roomsVisibleBeforePlacement.current !== null) {
        setRoomVisibility(roomsVisibleBeforePlacement.current);
        roomsVisibleBeforePlacement.current = null;
      }
    }

    // Cleanup: restore rooms on unmount if in placement mode
    return () => {
      if (roomsVisibleBeforePlacement.current !== null) {
        setRoomVisibility(roomsVisibleBeforePlacement.current);
        roomsVisibleBeforePlacement.current = null;
      }
    };
  }, [
    selectedTool,
    isControlPlacementMode,
    selectedProductCategory,
    isAnnotating,
    isCircuitMode,
    roomsVisible,
    setRoomVisibility,
  ]);

  // Reassign elements when room or fixture confidence threshold changes
  // NOTE: We explicitly do NOT include data.rooms in dependencies to avoid race conditions
  // with database foreign key constraints that automatically set room_id = NULL on room deletion.
  // This reassignment should only trigger when confidence thresholds change, not when rooms are added/deleted.
  useEffect(() => {
    // Skip reassignment if we don't have rooms or elements yet
    if (!data.rooms || data.rooms.length === 0) {
      return;
    }

    // Skip initial render (threshold changes from undefined to default value)
    if (
      aiTools.roomConfidenceThreshold === undefined ||
      aiTools.confidenceThreshold === undefined
    ) {
      return;
    }

    logger.log(
      `Confidence threshold changed (rooms: ${aiTools.roomConfidenceThreshold}, fixtures: ${aiTools.confidenceThreshold}), reassigning elements...`,
    );

    // Use a timeout to debounce rapid slider changes
    const timeoutId = setTimeout(async () => {
      try {
        const result = await reassignElementsBasedOnConfidence(
          floorPlan.id,
          aiTools.roomConfidenceThreshold,
          aiTools.confidenceThreshold, // Pass fixture confidence threshold
        );

        if (result.total > 0) {
          toast.success(
            `Reassigned ${result.total} element${result.total !== 1 ? 's' : ''} based on room visibility`,
          );

          // Refetch all element data to update the UI
          queryClient.invalidateQueries({ queryKey: queryKeys.fixtures(floorPlan.id) });
          queryClient.invalidateQueries({ queryKey: queryKeys.controls(floorPlan.id) });
          // LV products are managed by FloorPlanContext, refetch will happen via realtime
        } else {
          logger.log('No elements needed reassignment');
        }

        if (result.errors.length > 0) {
          toast.error(`Some elements failed to reassign: ${result.errors[0]}`);
        }
      } catch (error) {
        logger.error('Error reassigning elements based on confidence:', error);
        toast.error('Failed to reassign elements');
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [
    aiTools.roomConfidenceThreshold,
    aiTools.confidenceThreshold,
    floorPlan.id,
    // data.rooms deliberately excluded - see comment above
    queryClient,
  ]);

  // Store clipboard data
  const clipboardRef = useRef<{ type: 'annotations'; data: Annotation[] } | null>(null);

  // Handle spacebar for panning mode and ESC for clearing selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Get active drawer state
      const activeDrawer = useAppStore.getState().ui.activeDrawer;

      if (e.code === 'Space' && !e.repeat && !activeDrawer && !isPublicView) {
        // Check if we're editing text - check both event target and activeElement for robustness
        const target = e.target as HTMLElement;
        const activeElement = document.activeElement;

        const isEditingText =
          target?.tagName === 'TEXTAREA' ||
          target?.tagName === 'INPUT' ||
          target?.getAttribute('contenteditable') === 'true' ||
          target?.isContentEditable ||
          activeElement?.tagName === 'TEXTAREA' ||
          activeElement?.tagName === 'INPUT' ||
          activeElement?.getAttribute('contenteditable') === 'true';

        if (!isEditingText) {
          // Only enable space-to-pan when not editing text
          e.preventDefault();
          setSpacePressed(true); // Update local state
          setSpacePressedStore(true); // Update store state
        }
      } else if (e.code === 'Escape' && !isPublicView) {
        e.preventDefault();
        // If in edit mode, cancel and exit it
        if (isEditingFloorPlanImage) {
          // Cancel changes (revert to saved state)
          editWrapperRef.current?.cancel();
          const setEditingFloorPlanImage = useAppStore.getState().setEditingFloorPlanImage;
          setEditingFloorPlanImage(false);
          toast.info('Edit cancelled');
          // Also close any open drawer
          const setActiveDrawer = useAppStore.getState().setActiveDrawer;
          setActiveDrawer(null);
        } else {
          clearAllSelections();
          setActiveDrawer(null);
        }
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && !isPublicView) {
        // Don't handle delete if typing in an input or editing text annotation
        const isEditingText = useAppStore.getState().annotations.isEditingText;
        if (
          isEditingText ||
          document.activeElement?.tagName === 'INPUT' ||
          document.activeElement?.tagName === 'TEXTAREA'
        ) {
          return;
        }

        // NOTE: Keyboard delete for cable paths and junctions is DISABLED
        // to prevent accidental deletion. Use the drawer's Delete button instead,
        // which shows a confirmation dialog.

        // Handle delete for selected annotations
        const selectedAnnotations = useAppStore.getState().selection.annotations;

        if (selectedAnnotations.size > 0 && data.deleteAnnotation) {
          e.preventDefault();

          // Track batch delete for undo if multiple annotations
          if (selectedAnnotations.size > 1 && data.annotations) {
            const annotationsToDelete = data.annotations.filter(ann =>
              selectedAnnotations.has(ann.id),
            );
            addAction({
              type: 'BATCH_DELETE_ANNOTATIONS',
              timestamp: Date.now(),
              floorPlanId: floorPlan.id,
              data: {
                annotations: annotationsToDelete,
              },
            });
          }

          // Delete all selected annotations
          selectedAnnotations.forEach(async annotationId => {
            await data.deleteAnnotation(annotationId);
          });
          // Clear selection after deleting
          clearAllSelections();
        }
      } else if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !isPublicView) {
        if (e.shiftKey) {
          // Redo: Ctrl+Shift+Z or Cmd+Shift+Z
          e.preventDefault();
          if (canRedo) {
            performRedo();
          }
        } else {
          // Undo: Ctrl+Z or Cmd+Z
          e.preventDefault();
          if (canUndo) {
            performUndo();
          }
        }
      } else if (e.key === 'y' && (e.ctrlKey || e.metaKey) && !isPublicView) {
        // Alternative redo: Ctrl+Y or Cmd+Y
        e.preventDefault();
        if (canRedo) {
          performRedo();
        }
      } else if (e.key === 'c' && (e.ctrlKey || e.metaKey) && !isPublicView) {
        // Handle copy for selected annotations
        const selectedAnnotations = useAppStore.getState().selection.annotations;

        if (selectedAnnotations.size > 0 && data.annotations) {
          e.preventDefault();
          // Find selected annotations
          const annotationsToCopy = data.annotations.filter(a => selectedAnnotations.has(a.id));
          if (annotationsToCopy.length > 0) {
            clipboardRef.current = {
              type: 'annotations',
              data: annotationsToCopy,
            };
            toast.success(
              `Copied ${annotationsToCopy.length} annotation${annotationsToCopy.length > 1 ? 's' : ''}`,
            );
          }
        }
      } else if (e.key === 'v' && (e.ctrlKey || e.metaKey) && !isPublicView) {
        // Handle paste for annotations
        if (clipboardRef.current?.type === 'annotations' && clipboardRef.current.data.length > 0) {
          e.preventDefault();

          // Get the center of the viewport
          const viewportCenterX = (containerRef.current!.clientWidth / 2 - position.x) / zoom;
          const viewportCenterY = (containerRef.current!.clientHeight / 2 - position.y) / zoom;

          // Calculate the center of the copied annotations
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          clipboardRef.current.data.forEach(annotation => {
            switch (annotation.type) {
              case 'line':
              case 'arrow':
                minX = Math.min(minX, annotation.data.start!.x, annotation.data.end!.x);
                minY = Math.min(minY, annotation.data.start!.y, annotation.data.end!.y);
                maxX = Math.max(maxX, annotation.data.start!.x, annotation.data.end!.x);
                maxY = Math.max(maxY, annotation.data.start!.y, annotation.data.end!.y);
                break;
              case 'rectangle':
              case 'text':
              case 'image':
                minX = Math.min(minX, annotation.data.x!);
                minY = Math.min(minY, annotation.data.y!);
                maxX = Math.max(maxX, annotation.data.x! + (annotation.data.width || 100));
                maxY = Math.max(maxY, annotation.data.y! + (annotation.data.height || 50));
                break;
              case 'circle':
                minX = Math.min(minX, annotation.data.cx! - annotation.data.radius!);
                minY = Math.min(minY, annotation.data.cy! - annotation.data.radius!);
                maxX = Math.max(maxX, annotation.data.cx! + annotation.data.radius!);
                maxY = Math.max(maxY, annotation.data.cy! + annotation.data.radius!);
                break;
            }
          });

          const centerX = (minX + maxX) / 2;
          const centerY = (minY + maxY) / 2;
          const offsetX = viewportCenterX - centerX;
          const offsetY = viewportCenterY - centerY;

          // Create new annotations with offset positions
          const { createAnnotation } = data;
          if (createAnnotation) {
            // Use Promise.all to handle async operations
            const createPromises = clipboardRef.current.data.map(async annotation => {
              const newData = { ...annotation.data };

              switch (annotation.type) {
                case 'line':
                case 'arrow':
                  newData.start = {
                    x: annotation.data.start!.x + offsetX,
                    y: annotation.data.start!.y + offsetY,
                  };
                  newData.end = {
                    x: annotation.data.end!.x + offsetX,
                    y: annotation.data.end!.y + offsetY,
                  };
                  break;
                case 'rectangle':
                case 'text':
                case 'image':
                  newData.x = annotation.data.x! + offsetX;
                  newData.y = annotation.data.y! + offsetY;
                  break;
                case 'circle':
                  newData.cx = annotation.data.cx! + offsetX;
                  newData.cy = annotation.data.cy! + offsetY;
                  break;
              }

              return createAnnotation({
                floor_plan_id: floorPlan.id,
                type: annotation.type,
                data: newData,
                style: annotation.style,
              });
            });

            // Execute all create operations
            Promise.all(createPromises)
              .then(() => {
                toast.success(
                  `Pasted ${clipboardRef.current.data.length} annotation${clipboardRef.current.data.length > 1 ? 's' : ''}`,
                );
              })
              .catch(error => {
                logger.error('Failed to paste annotations:', error);
                toast.error('Failed to paste some annotations');
              });
          }
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        // Only prevent default if we were actually panning
        if (spacePressed) {
          e.preventDefault();
        }
        setSpacePressed(false);
        setSpacePressedStore(false); // Update store state
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    clearAllSelections,
    setActiveDrawer,
    spacePressed,
    setSpacePressedStore,
    isPublicView,
    isEditingFloorPlanImage,
    deletePath,
    deleteJunction,
  ]);

  // Use the zoom gestures hook
  const {
    handleMouseDown: originalHandleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    fitToScreen,
  } = useZoomGestures({
    containerRef,
    zoom,
    position,
    setZoom,
    setPosition,
    minZoom: 0.1,
    maxZoom: 10,
    isPanningEnabled: spacePressed || activeTool === 'pan',
    disablePan: activeTool === 'pan' ? false : isDragging && !isEditingFloorPlanImage, // Never disable pan when hand tool is active
    imageSize, // Pass the image size from store
    isAnimating: viewport.isAnimating, // 🔧 CRITICAL FIX: Pass isAnimating flag to block gesture updates during animations
  });

  // Wrapped mouse down handler to track panning state
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      originalHandleMouseDown(e);
    },
    [originalHandleMouseDown],
  );

  // Add global mouseup handler to reset panning state
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsPanning(false);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, []);

  const handleImageLoad = useCallback(
    (size: { width: number; height: number }) => {
      logger.log('FloorPlanViewport - Image loaded:', {
        size,
        floorPlanId: floorPlan.id,
        isCadFile: floorPlan.is_cad_file,
      });
      setImageSize(size);
      setImageLoaded(true);
      setInitialViewportSettled(false);
      clearInitialFitSchedule();

      // Calculate and save auto-scale for new floor plans
      handleMetadataImageLoad(size);

      // Fit in the background and reveal only after the centered viewport has painted.
      initialFitTimerRef.current = window.setTimeout(() => {
        logger.log('Calling fitToScreen after image load');
        fitToScreen();
        initialFitFrameRef.current = window.requestAnimationFrame(() => {
          initialFitSecondFrameRef.current = window.requestAnimationFrame(() => {
            initialFitTimerRef.current = null;
            initialFitFrameRef.current = null;
            initialFitSecondFrameRef.current = null;
            setInitialViewportSettled(true);
          });
        });
      }, 200);
    },
    [setImageSize, fitToScreen, floorPlan, handleMetadataImageLoad, clearInitialFitSchedule],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();

      // Get dragged item from app store
      const draggedItem = useAppStore.getState().ui.draggedItem;

      if (!draggedItem) return;
      const supabase = await getAuthenticatedSupabaseClient();

      // Calculate position on floor plan
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = (e.clientX - rect.left - position.x) / zoom;
      const y = (e.clientY - rect.top - position.y) / zoom;

      // Handle Portal.io instance drop
      if (draggedItem.type === 'portal-instance') {
        try {
          const {
            instanceId,
            productId,
            product,
            instanceIndex,
            itemData,
            isSplitItem,
            splitSuffix,
            splitCategory,
            splitId,
          } = draggedItem;

          // Determine name and category based on whether this is a split item
          const productName = isSplitItem
            ? `${product.brand} ${product.model} - ${splitSuffix} - Unit ${instanceIndex}`
            : `${product.brand} ${product.model} - Unit ${instanceIndex}`;

          const productCategory = splitCategory || product.category;

          // Helper function to map Controls subcategories to control types
          const getControlType = (subcategory: string | undefined): string => {
            const mapping: Record<string, string> = {
              Keypad: 'keypad',
              Dimmer: 'dimmer',
              Switch: 'switch',
              Auxiliary: 'auxiliary',
              'Centralized Keypad': 'centralized-keypad',
            };
            return mapping[subcategory || ''] || 'keypad'; // Default to keypad
          };

          // Check if this is a Controls category - create lighting_controls instead of lv_products
          // Handle both "control" and "controls" variations
          let placedItemId: string;
          let placedItemData: any;
          const isControlCategory =
            productCategory?.toLowerCase() === 'controls' ||
            productCategory?.toLowerCase() === 'control';

          if (isControlCategory) {
            // Create lighting control on floor plan
            const controlType = getControlType(product.subcategory);

            // Calculate sequence_number for centralized keypads
            let sequenceNumber: number | null = null;
            if (controlType === 'centralized-keypad') {
              try {
                // Get the project_id for this floor plan
                const { data: floorPlanData } = await supabase
                  .from('floor_plans')
                  .select('project_id')
                  .eq('id', floorPlan.id)
                  .single();

                if (floorPlanData?.project_id) {
                  // Get all floor plans in this project
                  const { data: projectFloorPlans } = await supabase
                    .from('floor_plans')
                    .select('id')
                    .eq('project_id', floorPlanData.project_id);

                  if (projectFloorPlans && projectFloorPlans.length > 0) {
                    const floorPlanIds = projectFloorPlans.map(fp => fp.id);

                    // Get all centralized keypads in this project
                    const { data: allKeypads } = await supabase
                      .from('lighting_controls')
                      .select('sequence_number')
                      .eq('type', 'centralized-keypad')
                      .in('floor_plan_id', floorPlanIds)
                      .order('sequence_number', { ascending: true });

                    if (allKeypads && allKeypads.length > 0) {
                      const usedNumbers = allKeypads
                        .map(k => k.sequence_number)
                        .filter(num => num !== null);

                      // Find the first gap or use max + 1
                      let foundGap = false;
                      for (let i = 1; i <= usedNumbers.length + 1; i++) {
                        if (!usedNumbers.includes(i)) {
                          sequenceNumber = i;
                          foundGap = true;
                          break;
                        }
                      }
                      if (!foundGap) {
                        sequenceNumber = Math.max(...usedNumbers) + 1;
                      }
                    } else {
                      sequenceNumber = 1;
                    }
                  } else {
                    sequenceNumber = 1;
                  }
                } else {
                  sequenceNumber = 1;
                }
              } catch (error) {
                logger.error('Error calculating sequence number:', error);
                sequenceNumber = 1;
              }
            }

            const { data: control, error: controlError } = await supabase
              .from('lighting_controls')
              .insert({
                floor_plan_id: floorPlan.id,
                product_id: productId,
                type: controlType,
                name: productName,
                x_position: x,
                y_position: y,
                number_of_buttons: controlType === 'centralized-keypad' ? 4 : 1,
                labels: controlType === 'centralized-keypad' ? ['', '', '', ''] : [''],
                actions: controlType === 'centralized-keypad' ? [{}, {}, {}, {}] : [{}],
                sequence_number: sequenceNumber,
                category_info: {
                  portal_instance_id: instanceId,
                  portal_product_id: product.id, // Add portal_product_id
                  from_portal: true,
                  portal_item_data: itemData,
                  is_split_item: isSplitItem,
                  split_suffix: splitSuffix,
                  split_id: splitId,
                  category: productCategory,
                  subcategory: product.subcategory,
                },
              })
              .select()
              .single();

            if (controlError) throw controlError;
            placedItemId = control.id;
            placedItemData = control;
          } else if (productCategory?.toLowerCase() === 'fixture') {
            // Get default scale from cache for consistent sizing
            const defaultScale = getDefaultScale('fixture', productCategory, product.subcategory);

            // Create lighting fixture on floor plan
            const { data: fixture, error: fixtureError } = await supabase
              .from('lighting_fixtures')
              .insert({
                floor_plan_id: floorPlan.id,
                product_id: productId,
                fixture_type: product.subcategory || 'other',
                custom_name: productName,
                category: productCategory,
                subcategory: product.subcategory,
                x_position: x,
                y_position: y,
                scale: defaultScale || 1.0, // Apply default scale for consistent sizing
                icon_style: 'icon', // Portal fixtures use icon style
                user_verified: true,
                category_info: {
                  portal_instance_id: instanceId,
                  portal_product_id: product.id,
                  from_portal: true,
                  portal_item_data: itemData,
                  is_split_item: isSplitItem,
                  split_suffix: splitSuffix,
                  split_id: splitId,
                  category: productCategory,
                  subcategory: product.subcategory,
                },
              })
              .select()
              .single();

            if (fixtureError) throw fixtureError;
            placedItemId = fixture.id;
            placedItemData = fixture;
          } else {
            // Create LV product on floor plan
            const { data: lvProduct, error } = await supabase
              .from('lv_products')
              .insert({
                floor_plan_id: floorPlan.id,
                product_id: productId, // Split items use parent's product_id
                category: productCategory,
                subcategory: product.subcategory,
                name: productName,
                x_position: x,
                y_position: y,
                metadata: {
                  portal_instance_id: instanceId,
                  from_portal: true,
                  portal_item_data: itemData,
                  is_split_item: isSplitItem,
                  split_suffix: splitSuffix,
                  split_id: splitId,
                },
              })
              .select()
              .single();

            if (error) throw error;
            placedItemId = lvProduct.id;
            placedItemData = lvProduct;
          }

          // Handle placement tracking differently for split items vs regular items
          if (isSplitItem && splitId) {
            // Update split item placement in portal_proposal_instances
            const { error: updateError } = await supabase.rpc('update_split_item_placement', {
              p_instance_id: instanceId,
              p_split_id: splitId,
              p_suffix: splitSuffix || '',
              p_placed: true,
              p_av_product_id: placedItemId,
              p_position_x: x,
              p_position_y: y,
            });

            if (updateError) {
              logger.error('Error updating split item placement:', updateError);
            }

            // Dispatch split-specific event
            window.dispatchEvent(
              new CustomEvent('portal-item-placed', {
                detail: {
                  instanceId: instanceId,
                  productId: placedItemId,
                  splitId: splitId,
                  splitSuffix: splitSuffix,
                  position_x: x,
                  position_y: y,
                },
              }),
            );
          } else {
            // Regular instance placement
            await supabase
              .from('portal_proposal_instances')
              .update({
                av_product_id: placedItemId,
                is_placed: true,
                placed_at: new Date().toISOString(),
                position_x: x,
                position_y: y,
              })
              .eq('id', instanceId);

            // Dispatch regular placement event
            window.dispatchEvent(
              new CustomEvent('portal-item-placed', {
                detail: {
                  instanceId: instanceId,
                  productId: placedItemId,
                },
              }),
            );
          }

          // Optimistically update cache for immediate UI feedback
          if (isControlCategory && placedItemData) {
            const queryKey = queryKeys.controls(floorPlan.id);
            queryClient.setQueryData(queryKey, (oldData: any) => {
              if (!oldData) return [placedItemData];
              return [...oldData, placedItemData];
            });
            // Invalidate to sync with server
            await queryClient.invalidateQueries({ queryKey });
          } else if (productCategory?.toLowerCase() === 'fixture' && placedItemData) {
            const queryKey = queryKeys.fixtures(floorPlan.id);
            queryClient.setQueryData(queryKey, (oldData: any) => {
              if (!oldData) return [placedItemData];
              return [...oldData, placedItemData];
            });
            // Invalidate to sync with server
            await queryClient.invalidateQueries({ queryKey });
          } else if (placedItemData) {
            const queryKey = queryKeys.entitiesByFloorPlan('lv_products', floorPlan.id);
            queryClient.setQueryData(queryKey, (oldData: any) => {
              if (!oldData) return [placedItemData];
              return [...oldData, placedItemData];
            });
            // Invalidate to sync with server
            await queryClient.invalidateQueries({ queryKey });
          }

          toast.success(`Placed ${productName}`);
        } catch (error) {
          logger.error('Error placing Portal.io instance:', error);
          toast.error('Failed to place item on floor plan');
        }
      }
      // Handle Portal product group drop (multiple split items)
      else if (draggedItem.type === 'portal-product-group') {
        try {
          const { products, autoSpace } = draggedItem;

          // Check which room the products are being placed in (only visible rooms based on confidence)
          const roomId = getRoomIdForPoint(
            { x, y },
            data.rooms || [],
            aiTools.roomConfidenceThreshold,
          );

          // Helper function to map Controls subcategories to control types
          const getControlType = (subcategory: string | undefined): string => {
            const mapping: Record<string, string> = {
              Keypad: 'keypad',
              Dimmer: 'dimmer',
              Switch: 'switch',
              Auxiliary: 'auxiliary',
              'Centralized Keypad': 'centralized-keypad',
            };
            return mapping[subcategory || ''] || 'keypad'; // Default to keypad
          };

          // Calculate spacing between items (30px default)
          const spacing = autoSpace ? 30 : 0;

          // Track which categories were used to invalidate correct caches
          // Handle both "control" and "controls" variations
          const hasControls = products.some(
            p =>
              p.category?.toLowerCase() === 'controls' || p.category?.toLowerCase() === 'control',
          );
          const hasFixtures = products.some(p => p.category?.toLowerCase() === 'fixture');
          const hasLVProducts = products.some(
            p =>
              p.category?.toLowerCase() !== 'controls' &&
              p.category?.toLowerCase() !== 'control' &&
              p.category?.toLowerCase() !== 'fixture',
          );

          // Calculate initial sequence numbers for centralized keypads if needed
          const centralizedKeypadCount = products.filter(
            p =>
              (p.category?.toLowerCase() === 'controls' ||
                p.category?.toLowerCase() === 'control') &&
              getControlType(p.subcategory) === 'centralized-keypad',
          ).length;

          let nextSequenceNumber: number | null = null;
          if (centralizedKeypadCount > 0) {
            try {
              // Get the project_id for this floor plan
              const { data: floorPlanData } = await supabase
                .from('floor_plans')
                .select('project_id')
                .eq('id', floorPlan.id)
                .single();

              if (floorPlanData?.project_id) {
                // Get all floor plans in this project
                const { data: projectFloorPlans } = await supabase
                  .from('floor_plans')
                  .select('id')
                  .eq('project_id', floorPlanData.project_id);

                if (projectFloorPlans && projectFloorPlans.length > 0) {
                  const floorPlanIds = projectFloorPlans.map(fp => fp.id);

                  // Get all centralized keypads in this project
                  const { data: allKeypads } = await supabase
                    .from('lighting_controls')
                    .select('sequence_number')
                    .eq('type', 'centralized-keypad')
                    .in('floor_plan_id', floorPlanIds)
                    .order('sequence_number', { ascending: true });

                  if (allKeypads && allKeypads.length > 0) {
                    const usedNumbers = allKeypads
                      .map(k => k.sequence_number)
                      .filter(num => num !== null);

                    // Start with the max + 1
                    nextSequenceNumber = Math.max(...usedNumbers) + 1;
                  } else {
                    nextSequenceNumber = 1;
                  }
                } else {
                  nextSequenceNumber = 1;
                }
              } else {
                nextSequenceNumber = 1;
              }
            } catch (error) {
              logger.error('Error calculating sequence number:', error);
              nextSequenceNumber = 1;
            }
          }

          // Place each product in the group
          const placedProducts = [];
          for (let i = 0; i < products.length; i++) {
            const product = products[i];

            // Calculate position with spacing
            const offsetX = x + i * spacing;
            const offsetY = y;

            // Prepare base metadata
            const baseMetadata: any = {
              from_portal: true,
              portal_product_id: product.portalProductId,
              portal_name: product.name,
              portal_brand: product.manufacturer,
              portal_model: product.model,
              is_split: true,
              split_suffix: product.splitSuffix,
              split_parent_id: product.splitParentId,
              split_parent_reference: product.splitParentReference,
            };

            // For split items, use the split-specific name (includes suffix or custom name)
            // For non-split items, use model name for consistency (existing behavior)
            const productName = product.isSplit
              ? product.name || product.model
              : product.model || product.name;

            // Check if this is a Controls category - create lighting_controls instead of lv_products
            const isProductControl =
              product.category?.toLowerCase() === 'controls' ||
              product.category?.toLowerCase() === 'control';
            if (isProductControl) {
              // Create lighting control on floor plan
              const controlType = getControlType(product.subcategory);

              // Assign and increment sequence number for centralized keypads
              let sequenceNumber: number | null = null;
              if (controlType === 'centralized-keypad' && nextSequenceNumber !== null) {
                sequenceNumber = nextSequenceNumber;
                nextSequenceNumber++;
              }

              // Get default scale for this control type
              const defaultScale = getDefaultScale('control', controlType, undefined);

              const { data: control, error: controlError } = await supabase
                .from('lighting_controls')
                .insert({
                  floor_plan_id: floorPlan.id,
                  product_id: product.productLibraryId || null,
                  type: controlType,
                  name: productName,
                  x_position: offsetX,
                  y_position: offsetY,
                  number_of_buttons: controlType === 'centralized-keypad' ? 4 : 1,
                  labels: controlType === 'centralized-keypad' ? ['', '', '', ''] : [''],
                  actions: controlType === 'centralized-keypad' ? [{}, {}, {}, {}] : [{}],
                  sequence_number: sequenceNumber,
                  scale: defaultScale || 1.0, // Apply default scale
                  category_info: {
                    category: product.category,
                    subcategory: product.subcategory,
                    from_portal: true,
                    portal_product_id: product.portalProductId,
                    portal_name: product.name,
                    portal_brand: product.manufacturer,
                    portal_model: product.model,
                    is_split: true,
                    split_suffix: product.splitSuffix,
                    split_parent_id: product.splitParentId,
                    split_parent_reference: product.splitParentReference,
                  },
                })
                .select()
                .single();

              if (controlError) throw controlError;
              placedProducts.push(control);
            } else if (product.category?.toLowerCase() === 'fixture') {
              // Get default scale for this fixture type
              // For Portal fixtures, subcategory contains the actual fixture category (e.g., "Downlight")
              const fixtureCategory = product.subcategory?.toLowerCase() || 'other';
              const defaultScale = getDefaultScale('fixture', fixtureCategory, undefined);

              // Create lighting fixture on floor plan
              const { data: fixture, error: fixtureError } = await supabase
                .from('lighting_fixtures')
                .insert({
                  floor_plan_id: floorPlan.id,
                  product_id: product.productLibraryId || null,
                  fixture_type: product.subcategory || 'other',
                  custom_name: productName,
                  category: product.category,
                  subcategory: product.subcategory,
                  x_position: offsetX,
                  y_position: offsetY,
                  room_id: roomId || null,
                  icon_style: 'icon', // Portal fixtures use icon style
                  user_verified: true,
                  scale: defaultScale || 1.0, // Apply default scale
                  category_info: {
                    category: product.category,
                    subcategory: product.subcategory,
                    from_portal: true,
                    portal_product_id: product.portalProductId,
                    portal_name: product.name,
                    portal_brand: product.manufacturer,
                    portal_model: product.model,
                    is_split: true,
                    split_suffix: product.splitSuffix,
                    split_parent_id: product.splitParentId,
                    split_parent_reference: product.splitParentReference,
                  },
                })
                .select()
                .single();

              if (fixtureError) throw fixtureError;
              placedProducts.push(fixture);
            } else {
              // Get default scale for this product type
              const productCategory = product.category?.toLowerCase() || 'other';
              const productSubcategory = product.subcategory?.toLowerCase() || undefined;
              const defaultScale = getDefaultScale('product', productCategory, productSubcategory);

              // Create LV product on floor plan
              const { data: lvProduct, error } = await supabase
                .from('lv_products')
                .insert({
                  floor_plan_id: floorPlan.id,
                  product_id: product.productLibraryId || null,
                  name: productName,
                  category: product.category || 'other',
                  subcategory: product.subcategory || null,
                  x_position: offsetX,
                  y_position: offsetY,
                  room_id: roomId || null,
                  scale: defaultScale || 1.0, // Apply default scale
                  metadata: baseMetadata,
                })
                .select()
                .single();

              if (error) throw error;
              placedProducts.push(lvProduct);
            }
          }

          // Invalidate the appropriate caches based on what was placed
          if (hasControls) {
            await queryClient.invalidateQueries({
              queryKey: queryKeys.controls(floorPlan.id),
            });
          }
          if (hasFixtures) {
            await queryClient.invalidateQueries({
              queryKey: queryKeys.fixtures(floorPlan.id),
            });
          }
          if (hasLVProducts) {
            await queryClient.invalidateQueries({
              queryKey: queryKeys.entitiesByFloorPlan('lv_products', floorPlan.id),
            });
          }

          // Show success message with product name/model instead of UUID
          const firstProduct = products[0];
          const itemName =
            firstProduct?.name ||
            (firstProduct?.manufacturer && firstProduct?.model
              ? `${firstProduct.manufacturer} ${firstProduct.model}`
              : 'items');
          toast.success(`Added ${products.length} ${itemName} to floor plan`);
        } catch (error) {
          logger.error('Error adding Portal product group to floor plan:', error);
          toast.error('Failed to add products to floor plan');
        }
      }
      // Handle Portal product drop (from PortalProductsDrawer)
      else if (draggedItem.type === 'portal-product') {
        try {
          const { product } = draggedItem;

          // Check which room the product is being placed in (only visible rooms based on confidence)
          const roomId = getRoomIdForPoint(
            { x, y },
            data.rooms || [],
            aiTools.roomConfidenceThreshold,
          );

          // Helper function to map Controls subcategories to control types
          const getControlType = (subcategory: string | undefined): string => {
            const mapping: Record<string, string> = {
              Keypad: 'keypad',
              Dimmer: 'dimmer',
              Switch: 'switch',
              Auxiliary: 'auxiliary',
              'Centralized Keypad': 'centralized-keypad',
            };
            return mapping[subcategory || ''] || 'keypad'; // Default to keypad
          };

          // Prepare base metadata
          const baseMetadata: any = {
            from_portal: true,
            portal_product_id: product.portalProductId,
            portal_name: product.name,
            portal_brand: product.manufacturer,
            portal_model: product.model,
          };

          // Add split metadata if this is a split item
          if (product.isSplit) {
            baseMetadata.is_split = true;
            baseMetadata.split_suffix = product.splitSuffix;
            baseMetadata.split_parent_id = product.splitParentId;
            baseMetadata.split_parent_reference = product.splitParentReference;
          }

          // Determine product name
          // For split items, use name first (includes suffix like "Sat 2 - Model")
          // For non-split items, use model first for consistency
          const productName = product.isSplit
            ? product.name || product.model || 'Portal Product'
            : product.model || product.name || 'Portal Product';

          // Check if this is a Controls category - create lighting_controls instead of lv_products
          // Handle both "control" and "controls" variations
          const isControlCategory =
            product.category?.toLowerCase() === 'controls' ||
            product.category?.toLowerCase() === 'control';

          if (isControlCategory) {
            // Create lighting control on floor plan
            const controlType = getControlType(product.subcategory);

            // Calculate sequence_number for centralized keypads
            let sequenceNumber: number | null = null;
            if (controlType === 'centralized-keypad') {
              try {
                // Get the project_id for this floor plan
                const { data: floorPlanData } = await supabase
                  .from('floor_plans')
                  .select('project_id')
                  .eq('id', floorPlan.id)
                  .single();

                if (floorPlanData?.project_id) {
                  // Get all floor plans in this project
                  const { data: projectFloorPlans } = await supabase
                    .from('floor_plans')
                    .select('id')
                    .eq('project_id', floorPlanData.project_id);

                  if (projectFloorPlans && projectFloorPlans.length > 0) {
                    const floorPlanIds = projectFloorPlans.map(fp => fp.id);

                    // Get all centralized keypads in this project
                    const { data: allKeypads } = await supabase
                      .from('lighting_controls')
                      .select('sequence_number')
                      .eq('type', 'centralized-keypad')
                      .in('floor_plan_id', floorPlanIds)
                      .order('sequence_number', { ascending: true });

                    if (allKeypads && allKeypads.length > 0) {
                      const usedNumbers = allKeypads
                        .map(k => k.sequence_number)
                        .filter(num => num !== null);

                      // Find the first gap or use max + 1
                      let foundGap = false;
                      for (let i = 1; i <= usedNumbers.length + 1; i++) {
                        if (!usedNumbers.includes(i)) {
                          sequenceNumber = i;
                          foundGap = true;
                          break;
                        }
                      }
                      if (!foundGap) {
                        sequenceNumber = Math.max(...usedNumbers) + 1;
                      }
                    } else {
                      sequenceNumber = 1;
                    }
                  } else {
                    sequenceNumber = 1;
                  }
                } else {
                  sequenceNumber = 1;
                }
              } catch (error) {
                logger.error('Error calculating sequence number:', error);
                sequenceNumber = 1;
              }
            }

            // Get default scale for this control type
            // For controls, we use the controlType (keypad, dimmer, etc.) not product.category
            const defaultScale = getDefaultScale('control', controlType, undefined);

            const { data: control, error: controlError } = await supabase
              .from('lighting_controls')
              .insert({
                floor_plan_id: floorPlan.id,
                product_id: product.productLibraryId || null,
                type: controlType,
                name: productName,
                x_position: x,
                y_position: y,
                number_of_buttons: controlType === 'centralized-keypad' ? 4 : 1,
                labels: controlType === 'centralized-keypad' ? ['', '', '', ''] : [''],
                actions: controlType === 'centralized-keypad' ? [{}, {}, {}, {}] : [{}],
                sequence_number: sequenceNumber,
                scale: defaultScale || 1.0, // Apply default scale or use 1.0
                category_info: {
                  category: product.category,
                  subcategory: product.subcategory,
                  from_portal: true,
                  portal_product_id: product.portalProductId,
                  portal_name: product.name,
                  portal_brand: product.manufacturer,
                  portal_model: product.model,
                  is_split: product.isSplit || false,
                  split_suffix: product.splitSuffix,
                  split_parent_id: product.splitParentId,
                  split_parent_reference: product.splitParentReference,
                },
              })
              .select()
              .single();

            if (controlError) throw controlError;

            // Invalidate the controls cache to force a refetch
            await queryClient.invalidateQueries({
              queryKey: queryKeys.controls(floorPlan.id),
            });

            // Show success message
            const itemName = product.isSplit
              ? product.name
              : product.manufacturer && product.model
                ? `${product.manufacturer} ${product.model}`
                : product.name;

            toast.success(`Added ${itemName} to floor plan`);
          } else if (product.category?.toLowerCase() === 'fixture') {
            // Get default scale for this fixture type
            // For Portal fixtures, subcategory contains the actual fixture category (e.g., "Downlight")
            const fixtureCategory = product.subcategory?.toLowerCase() || 'other';
            const defaultScale = getDefaultScale('fixture', fixtureCategory, undefined);

            // Create lighting fixture on floor plan
            const { data: fixture, error: fixtureError } = await supabase
              .from('lighting_fixtures')
              .insert({
                floor_plan_id: floorPlan.id,
                product_id: product.productLibraryId || null,
                fixture_type: product.subcategory || 'other',
                custom_name: productName,
                category: product.category,
                subcategory: product.subcategory,
                x_position: x,
                y_position: y,
                room_id: roomId || null,
                icon_style: 'icon', // Portal fixtures use icon style
                user_verified: true,
                scale: defaultScale || 1.0, // Apply default scale or use 1.0
                category_info: {
                  category: product.category,
                  subcategory: product.subcategory,
                  from_portal: true,
                  portal_product_id: product.portalProductId,
                  portal_name: product.name,
                  portal_brand: product.manufacturer,
                  portal_model: product.model,
                  is_split: product.isSplit || false,
                  split_suffix: product.splitSuffix,
                  split_parent_id: product.splitParentId,
                  split_parent_reference: product.splitParentReference,
                },
              })
              .select()
              .single();

            if (fixtureError) throw fixtureError;

            // Invalidate the fixtures cache to force a refetch
            await queryClient.invalidateQueries({
              queryKey: [...queryKeys.floorPlan(floorPlan.id), 'fixtures'],
            });

            // Show appropriate success message
            const itemName = product.isSplit
              ? product.name
              : product.manufacturer && product.model
                ? `${product.manufacturer} ${product.model}`
                : product.name;

            toast.success(`Added ${itemName} to floor plan`);
          } else {
            // Get default scale for this product type
            const productCategory = product.category?.toLowerCase() || 'other';
            const productSubcategory = product.subcategory?.toLowerCase() || undefined;
            const defaultScale = getDefaultScale('product', productCategory, productSubcategory);

            // Create LV product on floor plan
            const { data: lvProduct, error } = await supabase
              .from('lv_products')
              .insert({
                floor_plan_id: floorPlan.id,
                product_id: product.productLibraryId || null,
                name: productName,
                category: product.category || 'other',
                subcategory: product.subcategory || null,
                x_position: x,
                y_position: y,
                room_id: roomId || null,
                scale: defaultScale || 1.0, // Apply default scale or use 1.0
                metadata: baseMetadata,
              })
              .select()
              .single();

            if (error) throw error;

            // Invalidate the LV products cache to force a refetch
            await queryClient.invalidateQueries({
              queryKey: [...queryKeys.floorPlan(floorPlan.id), 'lv-products'],
            });

            // Show appropriate success message
            const itemName = product.isSplit
              ? product.name
              : product.manufacturer && product.model
                ? `${product.manufacturer} ${product.model}`
                : product.name;

            toast.success(`Added ${itemName} to floor plan`);
          }
        } catch (error) {
          logger.error('Error adding Portal product to floor plan:', error);
          toast.error('Failed to add product to floor plan');
        }
      }
      // Legacy Portal.io item handling (fallback)
      else if (draggedItem.type === 'portalio-item') {
        try {
          const { data: product, error } = await supabase
            .from('lv_products')
            .insert({
              floor_plan_id: floorPlan.id,
              name: draggedItem.item.name,
              category: draggedItem.item.category || 'other',
              x_position: x,
              y_position: y,
              metadata: {
                portal_item_id: draggedItem.item.id,
                brand: draggedItem.item.brand,
                model: draggedItem.item.model,
                price: draggedItem.item.sellPrice,
                quantity: draggedItem.item.quantity,
                from_portal: true,
              },
            })
            .select()
            .single();

          if (error) throw error;

          // Mark item as placed in legacy table
          await supabase
            .from('floor_plan_proposal_items')
            .update({ is_placed: true })
            .eq('floor_plan_id', floorPlan.id)
            .eq('portal_item_id', draggedItem.item.id);

          toast.success(`Added ${draggedItem.item.name} to floor plan`);
        } catch (error) {
          logger.error('Error adding Portal.io item to floor plan:', error);
          toast.error('Failed to add item to floor plan');
        }
      }

      // Clear dragged item
      useAppStore.getState().setDraggedItem(null);
    },
    [floorPlan, position, zoom, data],
  );

  const handleContainerClick = useCallback(
    async (e: React.MouseEvent | React.TouchEvent) => {
      // Stop simulation if it's running
      if (sim.running) {
        stopSim();
        return;
      }

      // Don't clear selection if we're dragging
      if (isDragging) return;

      // Don't clear selection if a selection is in progress
      if (isSelectionInProgress.current) {
        return;
      }

      const target = e.target as HTMLElement;

      // Only clear selection if clicking on the floor plan image or empty space
      // Check if we clicked on an interactive element
      const isInteractiveElement =
        target.closest('[data-fixture-id]') ||
        target.closest('[data-control-id]') ||
        target.closest('[data-panel-id]') ||
        target.closest('[data-room-id]') ||
        target.closest('[data-product-id]') ||
        target.closest('[data-rotation-handle]') ||
        target.closest('[data-fixture-badge-id]') ||
        target.closest('[data-control-badge-id]') ||
        target.closest('[data-handle-type]') ||
        target.closest('[data-path-handles]') ||
        target.closest('[data-path-id]') ||
        target.closest('[data-junction-id]');

      // Default behavior - clear selections and return to select tool (only in edit mode)
      if (
        !isInteractiveElement &&
        selectedTool !== 'create-fixture' &&
        !isControlPlacementMode &&
        !isProductPlacementMode &&
        !isPublicView &&
        !isMeasuring &&
        !isElevationMode &&
        activeTool !== 'pan'
      ) {
        // Check if we're in circuit mode (either from prop or store)
        const currentMode = useAppStore.getState().ui.toolbar.activeMode;

        if (currentMode === 'circuit' || isCircuitMode) {
          // In circuit mode, only clear selections, don't change mode or drawer
          clearAllSelections();
          // Clear hover state
          const setHoveredEntity = useAppStore.getState().setHoveredEntity;
          setHoveredEntity(null, null);
        } else {
          // Normal behavior for other modes
          clearAllSelections();
          setActiveDrawer(null);

          // Clear hover state
          const setHoveredEntity = useAppStore.getState().setHoveredEntity;
          setHoveredEntity(null, null);

          // Return to select tool
          const setActiveTool = useAppStore.getState().setActiveTool;
          const setActiveMode = useAppStore.getState().setActiveMode;
          setActiveTool('select');
          setActiveMode('default');
        }
      }
    },
    [
      clearAllSelections,
      setActiveDrawer,
      isDragging,
      sim.running,
      stopSim,
      selectedTool,
      isControlPlacementMode,
      isProductPlacementMode,
      isPublicView,
      isCircuitMode,
      isMeasuring,
      isElevationMode,
    ],
  );

  const handleViewportMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Don't handle if it's a right click or if we're currently dragging
      if (e.button !== 0 || isDragging) return;

      // Check if we clicked on an interactive element
      const target = e.target as HTMLElement;
      const isInteractiveElement =
        target.closest('[data-fixture-id]') ||
        target.closest('[data-control-id]') ||
        target.closest('[data-panel-id]') ||
        target.closest('[data-room-id]') ||
        target.closest('[data-product-id]') ||
        target.closest('[data-annotation-id]') ||
        target.closest('[data-rotation-handle]') ||
        target.closest('[data-handle-type]') ||
        target.closest('[data-path-handles]') ||
        target.closest('[data-path-id]') ||
        target.closest('[data-junction-id]');

      // If in annotation mode and clicking on interactive element, exit annotation mode
      if (isAnnotating && activeAnnotationType && isInteractiveElement) {
        const setActiveAnnotationType = useAppStore.getState().setActiveAnnotationType;
        setActiveAnnotationType(null);
        return; // Let the element handle the click
      }

      // Handle based on active tool
      if (spacePressed || activeTool === 'pan') {
        // Pan mode
        setIsPanning(true);
        handleMouseDown(e);
      } else if (activeTool === 'select' && !isEditingFloorPlanImage && !isAnnotating) {
        // Selection mode - only start selection if we didn't click on an interactive element and not editing floor plan
        if (!isInteractiveElement && selectionBoxRef.current) {
          selectionBoxRef.current.startSelection(e);
        }
      } else if (activeTool === 'draw') {
        // Drawing mode is handled by specific tool selections (create-fixture, create-room, etc.)
      } else if (activeTool === 'measure' || isMeasuring) {
        // Measuring mode is handled by the MeasurementOverlay component
        return;
      } else if (isAnnotating && activeAnnotationType) {
        // Annotation mode is handled by AnnotationDrawingOverlay
        return;
      }

      // Handle fixture/control placement on mouse down
      if (!isInteractiveElement && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - position.x) / zoom;
        const y = (e.clientY - rect.top - position.y) / zoom;

        // Handle unified placement from command palette
        if (placementItem) {
          e.preventDefault();
          e.stopPropagation();
          logger.log('[FloorPlanViewport] Unified placement triggered:', {
            type: placementItem.type,
            category: placementItem.category,
            subcategory: placementItem.subcategory,
            productLibraryId: placementItem.productLibraryId,
            displayName: placementItem.displayName,
          });
          (async () => {
            try {
              const roomId = getRoomIdForPoint(
                { x, y },
                data.rooms || [],
                aiTools.roomConfidenceThreshold,
              );

              if (placementItem.type === 'fixture') {
                // Get default scale
                const defaultScale = getDefaultScale(
                  'fixture',
                  placementItem.category || 'standard',
                  placementItem.subcategory,
                );

                const fixtureData: any = {
                  floor_plan_id: floorPlan.id,
                  x_position: x,
                  y_position: y,
                  fixture_type: placementItem.subcategory || 'standard',
                  wattage: 10,
                  confidence_score: 1.0,
                  user_verified: true,
                  scale: defaultScale || 1.0,
                  category: placementItem.category,
                  subcategory: placementItem.subcategory,
                  product_id: placementItem.productLibraryId || null,
                  custom_name: placementItem.displayName,
                };

                if (roomId) {
                  fixtureData.room_id = roomId;
                }

                logger.log('[FloorPlanViewport] Creating fixture with data:', fixtureData);
                const result = await data.createFixture(fixtureData, { showSuccessToast: false });
                logger.log('[FloorPlanViewport] Fixture created, result:', result);
                if (!result) throw new Error('Failed to create fixture');

                dispatchPlacementEvent(
                  'fixture',
                  placementItem.category,
                  placementItem.subcategory,
                );
              } else if (placementItem.type === 'control') {
                // Get default scale
                const controlType =
                  (placementItem.subcategory as
                    | 'keypad'
                    | 'dimmer'
                    | 'switch'
                    | 'auxiliary'
                    | 'centralized-keypad') || 'keypad';
                const defaultScale = getDefaultScale('control', controlType, undefined);
                const numberOfButtons =
                  controlType === 'keypad' || controlType === 'centralized-keypad' ? 3 : 1;

                const controlData = {
                  floorPlanId: floorPlan.id,
                  type: controlType,
                  xPosition: x,
                  yPosition: y,
                  name: placementItem.displayName,
                  numberOfButtons,
                  labels: Array(numberOfButtons).fill(''),
                  actions: createEmptyActions(numberOfButtons),
                  roomId: roomId || null,
                  scale: defaultScale || 1.0,
                  productId: placementItem.productLibraryId || null,
                };

                const result = await data.createControl(controlData, { showSuccessToast: false });
                if (!result) throw new Error('Failed to create control');

                dispatchPlacementEvent('control', placementItem.category);
              } else if (placementItem.type === 'product') {
                // Products from the product library - create as LV product
                const defaultScale = getDefaultScale(
                  'product',
                  placementItem.category || 'other',
                  placementItem.subcategory,
                );

                const lvProductData = {
                  floor_plan_id: floorPlan.id,
                  product_id: placementItem.productLibraryId || null,
                  category: placementItem.category || 'other',
                  subcategory: placementItem.subcategory || null, // Normalize undefined to null
                  name: placementItem.displayName,
                  x_position: x,
                  y_position: y,
                  scale: defaultScale || 1.0,
                  room_id: roomId || null,
                };

                logger.log('[FloorPlanViewport] Creating LV product with data:', lvProductData);
                const result = await data.createProduct(lvProductData, {
                  showSuccessToast: false,
                });
                logger.log('[FloorPlanViewport] LV product created, result:', result);
                if (!result) throw new Error('Failed to create product');

                dispatchPlacementEvent(
                  'product',
                  placementItem.category,
                  placementItem.subcategory,
                );
              }
            } catch (error) {
              logger.error('Failed to place item:', error);
              toast.error('Failed to place item');
            }
          })();
          return;
        }

        // Handle fixture creation
        if (selectedTool === 'create-fixture') {
          e.preventDefault();
          e.stopPropagation();
          (async () => {
            try {
              // Check if the fixture is being placed inside a room (only visible rooms based on confidence)
              const roomId = getRoomIdForPoint(
                { x, y },
                data.rooms || [],
                aiTools.roomConfidenceThreshold,
              );

              // Get default scale from cache (instant, no async)
              const defaultScale = getDefaultScale(
                'fixture',
                selectedFixtureCategory || 'standard',
                selectedFixtureSubcategory,
              );

              // Create fixture data with correct field names
              const fixtureData: any = {
                floor_plan_id: floorPlan.id,
                x_position: x,
                y_position: y,
                fixture_type: selectedFixtureSubcategory || 'standard', // Use subcategory as fixture_type
                wattage: 10,
                confidence_score: 1.0, // Set high confidence for user-created fixtures
                user_verified: true, // Mark as verified since user created it
                scale: defaultScale || 1.0, // Apply cached default scale immediately
              };

              // Add room_id if fixture is placed inside a room
              if (roomId) {
                fixtureData.room_id = roomId;
              }

              // Add category and subcategory if selected
              // Always add category and subcategory when they are provided
              if (selectedFixtureCategory) {
                fixtureData.category = selectedFixtureCategory;
              }
              if (selectedFixtureSubcategory) {
                fixtureData.subcategory = selectedFixtureSubcategory;
              }

              // Use optimistic mutation from context (has instant UI updates)
              const result = await data.createFixture(fixtureData, { showSuccessToast: false });

              if (!result) throw new Error('Failed to create fixture');

              // Dispatch placement event for counting
              dispatchPlacementEvent(
                'fixture',
                selectedFixtureCategory || 'default',
                selectedFixtureSubcategory || undefined,
              );

              // No need to refetch - optimistic updates handle this automatically
            } catch (error) {
              logger.error('Failed to create fixture:', error);
              toast.error('Failed to create fixture');
            }
          })();
          return;
        }

        // Handle control placement
        if (isControlPlacementMode && selectedControlType) {
          e.preventDefault();
          e.stopPropagation();
          (async () => {
            try {
              // Check if the control is being placed inside a room (only visible rooms based on confidence)
              const roomId = getRoomIdForPoint(
                { x, y },
                data.rooms || [],
                aiTools.roomConfidenceThreshold,
              );

              const numberOfButtons =
                selectedControlType === 'keypad' || selectedControlType === 'centralized-keypad'
                  ? 3
                  : 1;

              // Get default scale from cache (instant, no async)
              const defaultScale = getDefaultScale('control', selectedControlType, undefined);

              const controlData = {
                floorPlanId: floorPlan.id,
                type: selectedControlType,
                xPosition: x,
                yPosition: y,
                name: `${selectedControlType.charAt(0).toUpperCase() + selectedControlType.slice(1)}`,
                numberOfButtons,
                labels: Array(numberOfButtons).fill(''),
                actions: createEmptyActions(numberOfButtons),
                roomId: roomId || null,
                scale: defaultScale || 1.0, // Apply cached default scale immediately
              };

              logger.log('Creating control with data:', controlData);
              // Use optimistic mutation from context (has instant UI updates)
              const result = await data.createControl(controlData, { showSuccessToast: false });

              if (!result) {
                throw new Error('Failed to create control');
              }

              // Dispatch placement event for counting
              dispatchPlacementEvent('control', selectedControlType);

              // No need to refetch - optimistic updates handle this automatically
            } catch (error) {
              logger.error('Failed to create control:', error);
              toast.error('Failed to create control');
            }
          })();
          return;
        }

        // Handle product placement
        if (isProductPlacementMode && selectedProductCategory) {
          // Only prevent default if the event is cancelable (non-passive)
          if ('cancelable' in e && e.cancelable) {
            e.preventDefault();
          }
          e.stopPropagation();

          // Check if the product is being placed inside a room (only visible rooms based on confidence)
          const roomId = getRoomIdForPoint(
            { x, y },
            data.rooms || [],
            aiTools.roomConfidenceThreshold,
          );

          // Parse category and subcategory from the selected value
          let category = selectedProductCategory;
          let subcategory: string | undefined = undefined;

          if (selectedProductCategory?.includes(':')) {
            const [cat, subcat] = selectedProductCategory.split(':');
            category = cat;
            subcategory = subcat;
          }

          // If it's a group, show the name dialog first
          if (category === 'group') {
            setPendingGroupPosition({ x, y, roomId });
            setShowGroupNameDialog(true);
            return;
          }

          // For other products, create immediately
          (async () => {
            try {
              // Create a descriptive name based on category/subcategory
              const categoryNames: Record<string, string> = {
                camera: 'Camera',
                speaker: 'Speaker',
                access_point: 'Access Point',
                touch_panel: 'Touch Panel',
                doorbell: 'Doorbell',
                sensor: 'Sensor',
                thermostat: 'Thermostat',
                data: 'Data',
                shades: 'Shades',
                group: 'Group/Rack',
                switch: 'Switch',
                other: 'Product',
              };

              // Keep placement immediate: resolve labels synchronously so optimistic creation is
              // not blocked by category/template network lookups.
              const formatLabel = (value: string) =>
                value.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
              const displayName = subcategory
                ? `${categoryNames[category] || formatLabel(category)} - ${formatLabel(subcategory)}`
                : categoryNames[category] || formatLabel(category);

              // Get default scale from cache (instant, no async)
              const defaultScale = getDefaultScale('product', category, subcategory);

              const productData = {
                floor_plan_id: floorPlan.id,
                category: category,
                subcategory: subcategory || undefined,
                name: displayName,
                x_position: x,
                y_position: y,
                rotation: 0,
                scale: defaultScale || 1, // Apply cached default scale immediately
                room_id: roomId || undefined,
                category_info: {
                  display_label: displayName,
                  is_custom: false,
                  category: category,
                  subcategory: subcategory || undefined,
                },
              };

              logger.log('Creating LV product with data:', productData);
              // Use optimistic mutation from context (has instant UI updates)
              const result = await data.createProduct(productData, { showSuccessToast: false });

              if (!result) {
                throw new Error('Failed to create product');
              }

              // Dispatch placement event for counting
              dispatchPlacementEvent('product', category, subcategory || undefined);

              // No need to refetch - optimistic updates handle this automatically
            } catch (error) {
              logger.error('Failed to create product:', error);
              toast.error('Failed to create product');
            }
          })();
          return;
        }
      }
    },
    [
      addAction,
      spacePressed,
      handleMouseDown,
      isDragging,
      activeTool,
      selectedTool,
      isControlPlacementMode,
      selectedControlType,
      isProductPlacementMode,
      selectedProductCategory,
      floorPlan.id,
      position,
      zoom,
      data,
      isMeasuring,
      isAnnotating,
      activeAnnotationType,
    ],
  );

  // Handle viewport touch start for selection box and other tools
  const handleViewportTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // Track the number of touches at start
      touchStartCount.current = e.touches.length;

      // CRITICAL: If multi-touch detected in placement mode, stop any action
      if (
        (selectedTool === 'create-fixture' || isControlPlacementMode || isProductPlacementMode) &&
        e.touches.length > 1
      ) {
        // Note: We don't call preventDefault() here because React touch events are passive by default
        e.stopPropagation();
        logger.log('[Touch] Multi-touch prevented in placement mode');
        return;
      }

      // Don't handle if we're currently dragging or if it's multi-touch
      if (isDragging || e.touches.length !== 1) return;

      // IMPORTANT: For placement modes, extra check to prevent accidental placement during gestures
      if (
        (selectedTool === 'create-fixture' || isControlPlacementMode || isProductPlacementMode) &&
        e.touches.length > 1
      ) {
        logger.log(
          '[Touch] Multi-touch detected in placement mode - ignoring to prevent accidental placement',
        );
        return;
      }

      // Check if we touched an interactive element
      const target = e.target as HTMLElement;
      const isInteractiveElement =
        target.closest('[data-fixture-id]') ||
        target.closest('[data-control-id]') ||
        target.closest('[data-panel-id]') ||
        target.closest('[data-room-id]') ||
        target.closest('[data-product-id]') ||
        target.closest('[data-annotation-id]') ||
        target.closest('[data-handle-type]') ||
        target.closest('[data-path-handles]') ||
        target.closest('[data-path-id]') ||
        target.closest('[data-junction-id]');

      // If in annotation mode and touching interactive element, exit annotation mode
      if (isAnnotating && activeAnnotationType && isInteractiveElement) {
        const setActiveAnnotationType = useAppStore.getState().setActiveAnnotationType;
        setActiveAnnotationType(null);
        return; // Let the element handle the touch
      }

      // Handle based on active tool
      if (spacePressed || activeTool === 'pan') {
        // Pan mode - handled by useZoomGestures
        return;
      } else if (activeTool === 'select' && !isEditingFloorPlanImage && !isAnnotating) {
        // Selection mode - only start selection if we didn't touch an interactive element and not editing floor plan
        if (!isInteractiveElement && selectionBoxRef.current) {
          selectionBoxRef.current.startSelection(e);
        }
      } else if (isAnnotating && activeAnnotationType) {
        // Annotation mode is handled by AnnotationDrawingOverlay
        return;
      }

      // Handle fixture/control placement on touch with delay
      if (
        !isInteractiveElement &&
        containerRef.current &&
        (selectedTool === 'create-fixture' || isControlPlacementMode)
      ) {
        // SAFETY CHECK: Don't place if touch started with multiple fingers (was a gesture)
        if (touchStartCount.current > 1) {
          logger.log('[Touch] Ignoring placement - touch started with multiple fingers');
          return;
        }

        // Note: We don't call preventDefault() here because React touch events are passive by default
        // for better scrolling performance. The placement logic works without preventing default.
        e.stopPropagation();

        const touch = e.touches[0];
        const rect = containerRef.current.getBoundingClientRect();
        const x = (touch.clientX - rect.left - position.x) / zoom;
        const y = (touch.clientY - rect.top - position.y) / zoom;

        // Clear any existing timer
        if (placementDelayTimer.current) {
          clearTimeout(placementDelayTimer.current);
        }

        // Set a delay before placing to ensure it's not a gesture
        placementDelayTimer.current = setTimeout(() => {
          // Double-check touch count hasn't changed
          if (touchStartCount.current > 1) {
            logger.log('[Touch] Placement cancelled - multi-touch detected during delay');
            return;
          }

          // Handle fixture creation
          if (selectedTool === 'create-fixture') {
            (async () => {
              try {
                const roomId = getRoomIdForPoint(
                  { x, y },
                  data.rooms || [],
                  aiTools.roomConfidenceThreshold,
                );
                const fixtureData: any = {
                  floor_plan_id: floorPlan.id,
                  x_position: x,
                  y_position: y,
                  fixture_type: selectedFixtureSubcategory || 'standard',
                  wattage: 10,
                  confidence_score: 1.0,
                  room_id: roomId || null,
                  // Add category and subcategory if selected
                  category: selectedFixtureCategory || null,
                  subcategory: selectedFixtureSubcategory || null,
                };

                // Use optimistic mutation from context (has instant UI updates)
                const result = await data.createFixture(fixtureData, { showSuccessToast: false });
                if (!result) throw new Error('Failed to create fixture');

                // Dispatch placement event for counting
                dispatchPlacementEvent(
                  'fixture',
                  selectedFixtureCategory || 'default',
                  selectedFixtureSubcategory || undefined,
                );

                // No need to refetch - optimistic updates handle this automatically
              } catch (error) {
                logger.error('Failed to create fixture:', error);
                toast.error('Failed to create fixture');
              }
            })();
          }

          // Handle control placement
          if (isControlPlacementMode && selectedControlType) {
            (async () => {
              try {
                const roomId = getRoomIdForPoint(
                  { x, y },
                  data.rooms || [],
                  aiTools.roomConfidenceThreshold,
                );
                const numberOfButtons =
                  selectedControlType === 'keypad' || selectedControlType === 'centralized-keypad'
                    ? 3
                    : 1;

                const controlData = {
                  floorPlanId: floorPlan.id,
                  type: selectedControlType,
                  xPosition: x,
                  yPosition: y,
                  name: `${selectedControlType.charAt(0).toUpperCase() + selectedControlType.slice(1)}`,
                  numberOfButtons,
                  labels: Array(numberOfButtons).fill(''),
                  actions: createEmptyActions(numberOfButtons),
                  roomId: roomId || null,
                };

                // Use optimistic mutation from context (has instant UI updates)
                const result = await data.createControl(controlData, { showSuccessToast: false });
                if (!result) throw new Error('Failed to create control');

                // Dispatch placement event for counting
                dispatchPlacementEvent('control', selectedControlType);

                // No need to refetch - optimistic updates handle this automatically
              } catch (error) {
                logger.error('Failed to create control:', error);
                toast.error('Failed to create control');
              }
            })();
          }
        }, 150); // 150ms delay to distinguish from gesture start
      }
    },
    [
      isDragging,
      addAction,
      spacePressed,
      activeTool,
      selectedTool,
      isControlPlacementMode,
      selectedControlType,
      floorPlan.id,
      position,
      zoom,
      data,
      isAnnotating,
      activeAnnotationType,
    ],
  );

  // Calculate visible fixtures count
  const visibleFixturesCount = useMemo(() => {
    return (
      data.fixtures?.filter(f => {
        const confidence = f.confidence_score || 1;
        return confidence >= aiTools.confidenceThreshold;
      })?.length || 0
    );
  }, [data.fixtures, aiTools.confidenceThreshold]);

  // Calculate visible selected count
  const visibleSelectedCount = useMemo(() => {
    const visibleSelectedFixtures =
      data.fixtures?.filter(f => {
        if (!selectedFixtures.has(f.id)) return false;
        const confidence = f.confidence_score || 1;
        return confidence >= aiTools.confidenceThreshold;
      })?.length || 0;
    return visibleSelectedFixtures + selectedControls.size;
  }, [data.fixtures, selectedFixtures, selectedControls.size, aiTools.confidenceThreshold]);

  // Check if any rooms are selected (for vertex editing)
  const hasSelectedRooms = selectedRooms.size > 0;

  // Determine if we're in any edit mode that requires disabling browser touch handling
  const isInEditMode =
    selectedTool === 'create-room' ||
    selectedTool === 'create-fixture' ||
    selectedTool === 'create-panel' ||
    isControlPlacementMode ||
    isProductPlacementMode ||
    isMeasuring ||
    isEditingFloorPlanImage ||
    isAnnotating ||
    selectedFixtures.size > 0 ||
    selectedControls.size > 0;

  // Single return statement to maintain hook order - no early returns!
  return !data.imageUrl ? (
    <div className="flex-1 bg-white flex items-center justify-center">
      <div className="text-center">
        <p className="text-gray-500">Loading floor plan image...</p>
      </div>
    </div>
  ) : (
    <div
      ref={containerRef}
      id="floor-plan-viewport"
      className={`floor-plan-viewport relative w-full h-full overflow-hidden bg-white ${isInEditMode ? 'edit-mode' : 'view-mode'} ${hasSelectedRooms ? 'room-editing' : ''} ${isMeasuring ? 'measuring' : ''}`}
      style={{ cursor: getCursor() }}
      onMouseDown={e => {
        // Don't handle mousedown if we clicked on a rotation handle
        const target = e.target as HTMLElement;
        if (target.closest('[data-rotation-handle]')) {
          return;
        }
        handleViewportMouseDown(e);
      }}
      onTouchStart={handleViewportTouchStart}
      onTouchMove={e => {
        // Update touch count if more fingers are added
        if (e.touches.length > touchStartCount.current) {
          touchStartCount.current = e.touches.length;
        }
        // Don't call handleTouchMove in pan mode - native listeners handle it with { passive: false }
        if (!(spacePressed || activeTool === 'pan')) {
          handleTouchMove(e as any);
        }
      }}
      onTouchEnd={e => {
        // Don't call handleTouchEnd in pan mode - native listeners handle it with { passive: false }
        if (!(spacePressed || activeTool === 'pan')) {
          handleTouchEnd(e as any);
        }
        // Clear placement timer on touch end
        if (placementDelayTimer.current) {
          clearTimeout(placementDelayTimer.current);
          placementDelayTimer.current = null;
        }
        // Reset touch count when all touches end
        if (e.touches.length === 0) {
          touchStartCount.current = 0;
        }
      }}
      onClick={e => {
        // Don't handle click if we clicked on a rotation handle
        const target = e.target as HTMLElement;
        if (target.closest('[data-rotation-handle]')) {
          return;
        }
        handleContainerClick(e);
      }}
      onDragOver={e => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDrop={handleDrop}
    >
      {/* Show loading spinner while waiting for image and metadata */}
      {(!isFullyLoaded || !data.imageUrl) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-50">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      )}

      {/* Main Floor Plan - only render when fully loaded */}
      <div
        className="absolute inset-0"
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          zIndex: 2, // Above the dimming overlay
          opacity: isFullyLoaded ? 1 : 0, // Hide until fully loaded
          transition: 'opacity 200ms ease-in-out', // Smooth fade in
        }}
      >
        {data.imageUrl && (
          <SafariImageFix>
            <FloorPlanEditWrapper
              ref={editWrapperRef}
              floorPlanId={floorPlan.id}
              isActive={isEditingFloorPlanImage}
              imageSize={imageSize}
              viewport={viewport}
            >
              <FloorPlanCanvas
                imageUrl={data.imageUrl}
                svgUrl={data.svgUrl}
                onLoad={handleImageLoad}
                isSimulationRunning={sim.running}
                grayscale={floorPlanMetadata?.grayscale || false}
                opacity={temporaryImageOpacity ?? floorPlanMetadata?.image_opacity ?? 100}
              />
            </FloorPlanEditWrapper>
          </SafariImageFix>
        )}

        {isFullyLoaded && (
          <div style={{ opacity: isEditingFloorPlanImage ? 0.6 : 1 }}>
            {/* Entity Layers */}
            {logger.log('[FloorPlanViewport] data.fixtures:', data.fixtures)}
            {data.fixtures && (
              <FixtureLayer
                fixtures={data.fixtures}
                scale={zoom}
                onStartDrag={isPublicView ? undefined : startDrag}
                isDragging={isDragging}
                dragState={dragState}
                wasRecentlyDragged={wasRecentlyDragged}
                isPublicView={isPublicView}
              />
            )}

            {/* Strip Lighting Layer - renders linear fixtures */}
            {data.fixtures && (
              <StripLayer
                fixtures={data.fixtures}
                scale={zoom}
                onStartDrag={isPublicView ? undefined : startDrag}
                isDragging={isDragging}
                dragState={dragState}
                wasRecentlyDragged={wasRecentlyDragged}
                isPublicView={isPublicView}
                onEndpointDrag={isPublicView ? undefined : handleStripEndpointDrag}
                onVertexDrag={isPublicView ? undefined : handleStripVertexDrag}
                screenToImageCoordinates={isPublicView ? undefined : screenToImageCoordinates}
              />
            )}

            {data.controls && data.controls.length > 0 && (
              <ControlLayer
                controls={data.controls}
                scale={zoom}
                onStartDrag={isPublicView ? undefined : startDrag}
                isDragging={isDragging}
                dragState={dragState}
                wasRecentlyDragged={wasRecentlyDragged}
                isPublicView={isPublicView}
              />
            )}

            {/* Room polygons */}
            {data.rooms && (
              <RoomLayer
                rooms={data.rooms as any}
                scale={zoom}
                onStartDrag={isPublicView ? undefined : startDrag}
                isDragging={isDragging}
                dragState={dragState}
                wasRecentlyDragged={wasRecentlyDragged}
              />
            )}

            {/* Wall visualization for debugging */}
            {data.walls && <WallLayer walls={data.walls as any} scale={zoom} />}

            {/* LV Products */}
            {data.lvProducts && data.lvProducts.length > 0 && (
              <LVProductLayer
                scale={zoom}
                onStartDrag={isPublicView ? undefined : startDrag}
                isDragging={isDragging}
                dragState={dragState}
                wasRecentlyDragged={wasRecentlyDragged}
                isPublicView={isPublicView}
              />
            )}

            {/* Annotations */}
            <AnnotationLayer
              scale={zoom}
              onStartDrag={isPublicView ? undefined : startDrag}
              isDragging={isDragging}
              dragState={dragState}
              wasRecentlyDragged={wasRecentlyDragged}
              isPublicView={isPublicView}
            />

            {/* Load Connection Lines — dashed arcs between fixtures in the same circuit */}
            <svg
              style={{
                position: 'absolute',
                top: '-5000px',
                left: '-5000px',
                width: `${(imageSize.width || 1000) + 10000}px`,
                height: `${(imageSize.height || 1000) + 10000}px`,
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <g transform="translate(5000, 5000)">
                <LoadConnectionLayer scale={zoom} />
              </g>
            </svg>

            {/* Cable Paths and Junctions */}
            {/* IMPORTANT: pointerEvents must be 'none' on wrapper SVG to allow clicks to pass through
                to elements below (strips, fixtures). Individual cable paths and junctions set their own
                pointerEvents ('stroke' for paths, 'all' for junctions) for interactivity. */}
            <svg
              style={{
                position: 'absolute',
                top: '-5000px',
                left: '-5000px',
                width: `${(imageSize.width || 1000) + 10000}px`,
                height: `${(imageSize.height || 1000) + 10000}px`,
                pointerEvents: 'none',
                zIndex: 20,
              }}
            >
              <g transform="translate(5000, 5000)">
                <CableLayer
                  scale={zoom}
                  onStartDrag={isPublicView ? undefined : startDrag}
                  isDragging={isDragging}
                  dragState={dragState}
                  isPublicView={isPublicView}
                />
              </g>
            </svg>

            {/* Cable Path Edit Handles - Separate high-z-index layer for interactivity */}
            {!isPublicView && (
              <CablePathEditOverlay
                containerRef={containerRef}
                zoom={zoom}
                position={position}
                imageSize={imageSize}
                isPublicView={isPublicView}
              />
            )}

            {/* Floor Plan Legend - displays unique symbols used on the floor plan */}
            <FloorPlanLegend
              fixtures={data.fixtures || []}
              controls={data.controls || []}
              lvProducts={data.lvProducts || []}
              config={(floorPlanMetadata?.marker_sizes as MarkerSizesConfig)?.legend_config}
              scale={zoom}
              isPublicView={isPublicView}
              isSimRunning={sim.running}
              onConfigChange={handleLegendConfigChange}
            />

            {/* Unified dimming overlay for lighting simulation - covers ALL elements uniformly */}
            {sim.running && (
              <div
                className="absolute pointer-events-none"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.7)', // 70% black overlay for uniform dimming
                  zIndex: 400, // Above all dimmed elements (legend z-50, cables z-4), below lit fixtures (z-500)
                  animation: 'dimFadeIn 300ms ease-out forwards',
                  transform: 'translateZ(0)',
                  position: 'absolute',
                  top: -50000,
                  left: -50000,
                  right: -50000,
                  bottom: -50000,
                  width: 'auto',
                  height: 'auto',
                }}
              />
            )}
          </div>
        )}

        {/* Wall Selection Mode for Elevations - inside transformed container */}
        {isElevationMode &&
          data.rooms &&
          data.rooms.length > 0 &&
          (() => {
            // Match the same SVG dimensions as RoomLayer for proper alignment
            const svgPadding = 5000;
            const svgWidth = (imageSize.width || 1000) + svgPadding * 2;
            const svgHeight = (imageSize.height || 1000) + svgPadding * 2;

            return (
              <svg
                style={{
                  position: 'absolute',
                  top: `${-svgPadding}px`,
                  left: `${-svgPadding}px`,
                  width: `${svgWidth}px`,
                  height: `${svgHeight}px`,
                  pointerEvents: isElevationMode ? 'all' : 'none',
                  zIndex: 6, // Above RoomLayer (5) to ensure clicks work
                }}
                viewBox={`${-svgPadding} ${-svgPadding} ${svgWidth} ${svgHeight}`}
                preserveAspectRatio="none"
              >
                <WallSelectionMode
                  rooms={confidenceFilteredRooms.map((room: any) => ({
                    id: room.id,
                    name: room.name || 'Unnamed Room',
                    points: room.points,
                  }))}
                  scale={zoom}
                  scaleData={scaleData}
                  isActive={isElevationMode}
                  onWallSelect={handleWallSelect}
                  viewportTransform={{ x: position.x, y: position.y, scale: zoom }}
                  floorPlanId={floorPlan.id}
                />
              </svg>
            );
          })()}
      </div>

      {/* Annotation Drawing Overlay - render outside transformed container */}
      {!isPublicView && (
        <AnnotationDrawingOverlay
          floorPlanId={floorPlan.id}
          containerRef={containerRef}
          zoom={zoom}
          position={position}
          imageSize={imageSize}
          onComplete={() => {
            // Optionally handle completion
          }}
        />
      )}

      {/* Strip Drawing Overlay - for creating LED strip fixtures */}
      {!isPublicView && (
        <StripDrawingOverlay
          floorPlanId={floorPlan.id}
          containerRef={containerRef}
          isActive={selectedTool === 'create-strip'}
          zoom={zoom}
          position={position}
          imageSize={imageSize}
          onComplete={() => {
            onToolSelect?.('select');
          }}
          onCancel={() => {
            onToolSelect?.('select');
          }}
        />
      )}

      {/* Alignment Guides Overlay - shows snap lines during dragging */}
      {!isPublicView && isDragging && dragState?.snapPoints && (
        <AlignmentGuidesOverlay
          snapPoints={dragState.snapPoints}
          viewport={{
            scale: zoom,
            positionX: position.x,
            positionY: position.y,
          }}
          containerWidth={containerRef.current?.clientWidth || 0}
          containerHeight={containerRef.current?.clientHeight || 0}
          formatMeasurement={formatMeasurement}
        />
      )}

      {/* Selection box layer - render outside transformed container but inside viewport */}
      {!isPublicView &&
        !spacePressed &&
        !isDragging &&
        isFullyLoaded &&
        selectedTool !== 'create-room' &&
        selectedTool !== 'create-strip' &&
        !isMeasuring &&
        !isEditingFloorPlanImage && (
          <SelectionBox
            ref={selectionBoxRef}
            onSelectionStart={() => {
              isSelectionInProgress.current = true;
            }}
            onSelectionComplete={() => {
              // Clear flag after a very short delay to let the drawer open
              requestAnimationFrame(() => {
                isSelectionInProgress.current = false;
              });
            }}
          />
        )}

      {/* Room Creation Overlay */}
      {!isPublicView && (
        <RoomCreationOverlay
          floorPlanId={floorPlan.id}
          isActive={selectedTool === 'create-room'}
          containerRef={containerRef}
          zoom={zoom}
          position={position}
          onComplete={() => {
            // Notify parent component to return to select tool
            if (onProcessComplete) {
              onProcessComplete();
            }
          }}
          imageSize={imageSize}
        />
      )}

      {/* Measurement Overlay */}
      {!isPublicView && (
        <MeasurementOverlay
          key={scaleData ? JSON.stringify(scaleData) : 'no-scale'}
          floorPlanId={floorPlan.id}
          isActive={isMeasuring}
          containerRef={containerRef}
          onMeasurementComplete={onMeasurementComplete}
          setIsMeasuring={setIsMeasuring}
        />
      )}
      {/* Image Annotation Uploader */}
      {!isPublicView && (
        <ImageAnnotationUploader
          floorPlanId={floorPlan.id}
          containerRef={containerRef}
          zoom={zoom}
          position={position}
          onComplete={() => {
            // Optionally handle completion
          }}
        />
      )}

      {/* Cable Path Drawing Overlay */}
      {!isPublicView && (
        <CablePathDrawingOverlay containerRef={containerRef} zoom={zoom} position={position} />
      )}

      {/* Cable Box Placement Overlay */}
      {!isPublicView && (
        <CableBoxPlacementOverlay containerRef={containerRef} zoom={zoom} position={position} />
      )}

      {/* Stats overlay - commented out as it interferes with room labels */}
      {/* <div className="absolute top-4 left-4 bg-white p-2 rounded shadow pointer-events-none z-10">
        <p className="text-sm text-gray-600">
          Fixtures: {visibleFixturesCount} | 
          Controls: {data.controls?.length || 0} |
          Selected: {visibleSelectedCount}
          {spacePressed && ' | (Space + Drag to Pan)'}
          {isDragging && ' | (Dragging)'}
        </p>
      </div> */}

      {/* Tool Status Indicator - hide in public view */}
      {!isPublicView && (
        <ToolStatusIndicator
          selectedTool={selectedTool}
          isCircuitMode={isCircuitMode}
          isControlPlacementMode={isControlPlacementMode}
          selectedControlType={selectedControlType}
        />
      )}

      {/* Additional status for custom tools - hide in public view */}
      {!isPublicView && (selectedTool === 'create-fixture' || isControlPlacementMode) && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded shadow-lg pointer-events-none z-20">
          <p className="text-sm font-medium">
            {selectedTool === 'create-fixture' && 'Click to place fixture'}
            {isControlPlacementMode &&
              selectedControlType &&
              `Click to place ${selectedControlType}`}
          </p>
          <p className="text-xs opacity-80">Press ESC to cancel</p>
        </div>
      )}

      {/* Group Name Dialog */}
      <GroupNameDialog
        open={showGroupNameDialog}
        onOpenChange={setShowGroupNameDialog}
        onConfirm={handleGroupCreate}
      />

      {/* Floor Plan Edit Controls */}
      {isEditingFloorPlanImage && !isPublicView && (
        <FloorPlanEditControls
          floorPlanId={floorPlan.id}
          onUndo={() => editWrapperRef.current?.undo()}
          onReset={() => editWrapperRef.current?.reset()}
          onSave={() => editWrapperRef.current?.save() || Promise.resolve()}
        />
      )}

      {/* Elevation Creation Modal */}
      {selectedWallData && (
        <ElevationCreationModal
          open={showElevationModal}
          onOpenChange={setShowElevationModal}
          roomId={selectedWallData.roomId}
          roomName={selectedWallData.roomName}
          wall={selectedWallData.wall}
          wallLength={selectedWallData.wallLength}
          scaleData={scaleData}
          floorPlanId={floorPlan.id}
          onElevationCreated={handleElevationCreated}
        />
      )}
    </div>
  );
}
